import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SparkRenderer, SplatMesh } from '@ydrive/sparky';
import { pickWorldPointFromPointer } from '../utils/viewerSpatialUtils';

// ── Sky-colour sampling ───────────────────────────────────────────────────────
function sampleSkyColor(dataUrl: string): Promise<THREE.Color> {
  const fallback = new THREE.Color(0x8bb8d4);
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        const ctx = c.getContext('2d');
        if (!ctx) { resolve(fallback); return; }
        ctx.drawImage(img, 0, 0);
        const topH = Math.max(1, Math.floor(img.height * 0.10));
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 1; i <= 5; i++) {
          const x = Math.floor(img.width * i / 6);
          const y = Math.floor(topH / 2);
          const d = ctx.getImageData(x, y, 1, 1).data;
          const lum = (d[0] + d[1] + d[2]) / 3;
          if (lum > 30 && lum < 245) { r += d[0]; g += d[1]; b += d[2]; count++; }
        }
        resolve(count > 0
          ? new THREE.Color(r / count / 255, g / count / 255, b / count / 255)
          : fallback,
        );
      } catch { resolve(fallback); }
    };
    img.onerror = () => resolve(fallback);
    img.src = dataUrl;
  });
}

// ── Geometry helpers ──────────────────────────────────────────────────────────

/** Ray-casting point-in-polygon test for a 2D screen-space polygon. */
function pointInPolygon(pt: { x: number; y: number }, poly: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if (((yi > pt.y) !== (yj > pt.y)) && (pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}

// ── Splat mask helpers ────────────────────────────────────────────────────────

// Grid cell size in world-units. Large enough that even a 5 m brush radius
// queries at most 3×3×3 = 27 cells; small enough to keep per-cell candidate
// counts manageable in dense aerial scenes.
const ERASE_GRID_CELL = 5;

/**
 * Spatial hash grid built once from a SplatMesh's world-space positions.
 * queryRadius() is O(candidates) instead of O(totalSplats), so the brush
 * RAF loop no longer stalls on multi-million-splat aerial reconstructions.
 */
class SplatSpatialGrid {
  private cells = new Map<string, number[]>();
  // Flat typed arrays for cache-friendly distance checks during queries.
  private wx: Float32Array;
  private wy: Float32Array;
  private wz: Float32Array;

  constructor(splatMesh: SplatMesh) {
    const wxList: number[] = [];
    const wyList: number[] = [];
    const wzList: number[] = [];
    const wMat = splatMesh.matrixWorld;
    const v    = new THREE.Vector3();

    splatMesh.forEachSplat((idx, localCenter) => {
      v.copy(localCenter).applyMatrix4(wMat);
      wxList[idx] = v.x;
      wyList[idx] = v.y;
      wzList[idx] = v.z;
      const key  = `${Math.floor(v.x / ERASE_GRID_CELL)},${Math.floor(v.y / ERASE_GRID_CELL)},${Math.floor(v.z / ERASE_GRID_CELL)}`;
      const cell = this.cells.get(key);
      if (cell) cell.push(idx);
      else this.cells.set(key, [idx]);
    });

    this.wx = new Float32Array(wxList);
    this.wy = new Float32Array(wyList);
    this.wz = new Float32Array(wzList);
  }

  queryRadius(cx: number, cy: number, cz: number, radius: number): number[] {
    const out  : number[] = [];
    const rSq   = radius * radius;
    const minGX = Math.floor((cx - radius) / ERASE_GRID_CELL);
    const maxGX = Math.floor((cx + radius) / ERASE_GRID_CELL);
    const minGY = Math.floor((cy - radius) / ERASE_GRID_CELL);
    const maxGY = Math.floor((cy + radius) / ERASE_GRID_CELL);
    const minGZ = Math.floor((cz - radius) / ERASE_GRID_CELL);
    const maxGZ = Math.floor((cz + radius) / ERASE_GRID_CELL);

    for (let gx = minGX; gx <= maxGX; gx++) {
      for (let gy = minGY; gy <= maxGY; gy++) {
        for (let gz = minGZ; gz <= maxGZ; gz++) {
          const cell = this.cells.get(`${gx},${gy},${gz}`);
          if (!cell) continue;
          for (const idx of cell) {
            const dx = this.wx[idx] - cx;
            const dy = this.wy[idx] - cy;
            const dz = this.wz[idx] - cz;
            if (dx * dx + dy * dy + dz * dz <= rSq) out.push(idx);
          }
        }
      }
    }
    return out;
  }
}

/** Find all splat indices within `radius` world-units of `center`.
 *  Used only for one-shot sphere queries (eraseRegion prop). */
function scanSplatsInSphere(
  splatMesh: SplatMesh,
  center: THREE.Vector3,
  radius: number,
): number[] {
  const out: number[] = [];
  const wMat = splatMesh.matrixWorld;
  const rSq  = radius * radius;
  const wld  = new THREE.Vector3();
  splatMesh.forEachSplat((index, localCenter) => {
    wld.copy(localCenter).applyMatrix4(wMat);
    if (wld.distanceToSquared(center) <= rSq) out.push(index);
  });
  return out;
}

function scanSplatsInBox(splatMesh: SplatMesh, min: THREE.Vector3, max: THREE.Vector3): number[] {
  const out: number[] = [];
  const wMat = splatMesh.matrixWorld;
  const wld  = new THREE.Vector3();
  splatMesh.forEachSplat((index, localCenter) => {
    wld.copy(localCenter).applyMatrix4(wMat);
    if (wld.x >= min.x && wld.x <= max.x &&
        wld.y >= min.y && wld.y <= max.y &&
        wld.z >= min.z && wld.z <= max.z) out.push(index);
  });
  return out;
}

/** Union all zone index sets and push the result to the GPU mask. */
function rebuildMask(splatMesh: SplatMesh, zones: EraseZone[]): void {
  const set = new Set<number>();
  for (const z of zones) for (const idx of z.indices) set.add(idx);
  if (set.size === 0) { splatMesh.clearSplatMask(); return; }
  splatMesh.setSplatMask(new Uint32Array([...set]));
}

export interface PlaceableAsset {
  id: string;
  label: string;
  url: string;
  icon?: string;
  source?: 'trellis';
  format?: 'glb' | 'splat';
  fallbackUrl?: string;
  /** When set, the initial scale is chosen so the model is exactly this tall (metres). */
  targetHeight?: number;
}
type ToolMode = 'add' | 'erase' | 'paint' | 'lasso';
type AddStep  = 'selecting' | 'capturing' | 'loading' | 'interactive';

// ── Shared geometry (never disposed per-object) ────────────────────────────────
const HANDLE_GEOM = new THREE.SphereGeometry(1, 8, 6);
const CORNER_SIGNS: [number, number, number][] = [
  [-1,-1,-1],[1,-1,-1],[-1,1,-1],[1,1,-1],
  [-1,-1, 1],[1,-1, 1],[-1,1, 1],[1,1, 1],
];

// ── Add-mode handle helpers ───────────────────────────────────────────────────
function cameraHandleRadius(root: THREE.Object3D, camera: THREE.Camera): number {
  return Math.max(camera.position.distanceTo(root.position) * 0.03, 0.05);
}
function makeHandles(root: THREE.Object3D, camera: THREE.Camera, scene: THREE.Scene): THREE.Mesh[] {
  const box = new THREE.Box3().setFromObject(root);
  const r   = cameraHandleRadius(root, camera);
  return CORNER_SIGNS.map(([sx, sy, sz]) => {
    const mesh = new THREE.Mesh(
      HANDLE_GEOM,
      new THREE.MeshBasicMaterial({ color: 0xFDBA59, depthTest: false }),
    );
    mesh.scale.setScalar(r);
    mesh.position.set(
      sx < 0 ? box.min.x : box.max.x,
      sy < 0 ? box.min.y : box.max.y,
      sz < 0 ? box.min.z : box.max.z,
    );
    mesh.renderOrder = 999;
    scene.add(mesh);
    return mesh;
  });
}
function repositionHandles(handles: THREE.Mesh[], root: THREE.Object3D): void {
  const box = new THREE.Box3().setFromObject(root);
  CORNER_SIGNS.forEach(([sx, sy, sz], i) => {
    handles[i].position.set(
      sx < 0 ? box.min.x : box.max.x,
      sy < 0 ? box.min.y : box.max.y,
      sz < 0 ? box.min.z : box.max.z,
    );
  });
}
function rescaleHandles(handles: THREE.Mesh[], root: THREE.Object3D, camera: THREE.Camera): void {
  const r = cameraHandleRadius(root, camera);
  handles.forEach(h => h.scale.setScalar(r));
}

// ── GPU disposal ──────────────────────────────────────────────────────────────
function disposeModelGeometry(model: THREE.Object3D): void {
  const disposedTextures = new Set<THREE.Texture>();
  model.traverse(child => {
    if (!(child as THREE.Mesh).isMesh) return;
    const mesh = child as THREE.Mesh;
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    (mats as THREE.Material[]).forEach(m => {
      if (!m) return;
      Object.values(m).forEach(value => {
        if (value instanceof THREE.Texture && !disposedTextures.has(value)) {
          value.dispose();
          disposedTextures.add(value);
        }
      });
      m.dispose();
    });
  });
}

function disposePlacedObject(obj: PlacedObject): void {
  obj.scene.remove(obj.root, obj.boxHelper, ...obj.handles);
  obj.splatMesh?.dispose();
  disposeModelGeometry(obj.model); // disposes geometry, active materials, and their textures
  // Dispose the inactive material set (only relevant for Trellis assets where unlit ≠ pbr).
  for (const mm of obj.meshMaterials) {
    if (mm.unlit === mm.pbr) continue;
    const inactive = obj.shadingMode === 'unlit' ? mm.pbr : mm.unlit;
    const mats = Array.isArray(inactive) ? inactive as THREE.Material[] : [inactive as THREE.Material];
    mats.forEach(m => m?.dispose()); // textures already disposed above, just free material
  }
  obj.boxHelper.geometry.dispose();
  (obj.boxHelper.material as THREE.Material)?.dispose();
  obj.handles.forEach(h => (h.material as THREE.Material)?.dispose());
}

// ── Types ─────────────────────────────────────────────────────────────────────
type ShadingMode = 'unlit' | 'pbr';

/** Per-mesh material snapshot so shading can be toggled after placement. */
interface MeshMaterials {
  mesh:  THREE.Mesh;
  unlit: THREE.Material | THREE.Material[];
  pbr:   THREE.Material | THREE.Material[];
}

interface PlacedObject {
  id:           string;
  root:         THREE.Object3D;
  model:        THREE.Object3D;
  boxHelper:    THREE.BoxHelper;
  handles:      THREE.Mesh[];
  rawSize:      THREE.Vector3;
  localMinY:    number;
  surfaceY:     number;
  scale:        number;
  rotationY:    number;
  label:        string;
  icon:         string;
  scene:        THREE.Scene;
  shadingMode:  ShadingMode;
  meshMaterials: MeshMaterials[];
  splatMesh?:    SplatMesh;
}

/** One brush stroke = one erase zone. No 3D gizmo; mask is the feedback. */
interface EraseZone {
  id:      string;
  indices: number[];
}

/** One paint brush stroke = one zone; stores the Three.js patch meshes for undo. */
interface PaintZone {
  id:     string;
  color:  string;
  meshes: THREE.Mesh[];
  scene:  THREE.Scene;
}

interface ActiveDrag {
  type:          'move' | 'resize';
  objectId:      string;
  xzOffset?:    { x: number; z: number };
  startClientY?: number;
  startScale?:   number;
}

interface SavedControls {
  controls:      unknown;
  movementSpeed: number;
  lookSpeed:     number;
}

export interface EditSceneToolProps {
  viewerRef:      React.MutableRefObject<unknown>;
  isActive:       boolean;
  onClose:        () => void;
  anchorRef?:     React.RefObject<HTMLButtonElement | null>;
  assets?:        PlaceableAsset[];
  initialAssetId?: string;
  /** Agent-driven: select an already placed object by its user-facing label. */
  editAssetLabel?: string;
  eraseRegion?:   { center: THREE.Vector3; radius: number };
  onEraseRegionDismissed?: () => void;
  /** Agent-driven: accumulated list of AABBs to erase. Entries are appended, never replaced,
   *  so all previous erases remain masked when a new one arrives. */
  eraseBoxes?: Array<{ id: string; min: THREE.Vector3; max: THREE.Vector3 }>;
  /** Agent-driven: auto-load and scale this catalogue model to fit the given bounds. */
  agentPlacement?: {
    catalogueId?: string;
    label: string;
    targetBounds?: { min: THREE.Vector3; max: THREE.Vector3 };
    dimensions?: { width?: number; depth?: number; length?: number };
    /** When set, load this GLB directly instead of looking up catalogueId. */
    glbUrl?: string;
  };
  onAgentPlacementDone?: () => void;
  /** Called when the user presses Done with at least one placed object — passes the
   *  world-space center of the most recently active placed object so callers can use
   *  it as a flythrough target without relying on stale scene knowledge. */
  onModelPlaced?: (worldPos: [number, number, number]) => void;
  /** Called immediately after each object is added to the scene (before interactive adjustment).
   *  Passes the root Object3D whose position is updated live during dragging/resizing. */
  onObjectPlaced?: (root: THREE.Object3D, assetId: string) => void;
  /** When true the scene is aerial — default placement scale is boosted so
   *  objects aren't invisible from a high-altitude camera. */
  isAerial?: boolean;
  /** Increment this value to open the tool directly in erase mode (agent-driven). */
  eraseModeToken?: number;
}

const loader = new GLTFLoader();
let _nextId  = 0;
const genId  = () => String(++_nextId);

// ── Component ─────────────────────────────────────────────────────────────────
const EditSceneTool: React.FC<EditSceneToolProps> = ({ viewerRef, isActive, onClose, anchorRef, assets = [], initialAssetId, editAssetLabel, eraseRegion, onEraseRegionDismissed, eraseBoxes, agentPlacement, onAgentPlacementDone, onModelPlaced, onObjectPlaced, isAerial = false, eraseModeToken = 0 }) => {
  const availableAssets = React.useMemo<PlaceableAsset[]>(() => assets, [assets]);
  const [mode, setMode]                   = useState<ToolMode>('add');
  const [addStep, setAddStep]             = useState<AddStep>('selecting');
  const [selectedId, setSelectedId]       = useState<string | null>(null);
  const [placedObjects, setPlacedObjects] = useState<PlacedObject[]>([]);
  const [activeAddId, setActiveAddId]     = useState<string | null>(null);
  const [eraseZones, setEraseZones]       = useState<EraseZone[]>([]);
  const [activeEraseId, setActiveEraseId] = useState<string | null>(null);
  const [isBuildingGrid, setIsBuildingGrid] = useState(false);
  const [brushRadius, setBrushRadius]         = useState(0.5);  // metres (erase)
  const [paintZones, setPaintZones]           = useState<PaintZone[]>([]);
  const [activePaintId, setActivePaintId]     = useState<string | null>(null);
  const [paintColor, setPaintColor]           = useState('#7a9a5a'); // default grass
  const [paintBrushRadius, setPaintBrushRadius] = useState(0.3);    // metres
  const [lassoPts, setLassoPts]               = useState<{ x: number; y: number }[]>([]);
  const [lassoReady, setLassoReady]           = useState(false);
  // AABB of the region that was just erased — drives the "fill void" post-erase prompt.
  const [pendingVoidAabb, setPendingVoidAabb] = useState<{ min: THREE.Vector3; max: THREE.Vector3 } | null>(null);

  // Authoritative registries — updated synchronously before setState.
  const placedRef    = useRef<PlacedObject[]>([]);
  const eraseRef     = useRef<EraseZone[]>([]);
  const paintZonesRef = useRef<PaintZone[]>([]);

  // Tracks which initialAssetId we last acted on — prevents re-firing the
  // mode-switch when availableAssets changes while initialAssetId is unchanged.
  const lastActedInitialAssetIdRef = useRef<string | undefined>(undefined);
  // Stable ref so the effect can read availableAssets without it being a dep.
  const availableAssetsRef = useRef(availableAssets);
  // Prevents re-applying eraseRegion on every render when the prop reference is stable.
  const appliedEraseRegionRef   = useRef<{ center: THREE.Vector3; radius: number } | null>(null);
  const appliedEraseBoxIdsRef   = useRef(new Set<string>());
  const appliedAgentPlacementRef = useRef<typeof agentPlacement | null>(null);

  const activeDragRef    = useRef<ActiveDrag | null>(null);
  const moveRafRef       = useRef<number | null>(null);
  const placeDownRef     = useRef<{ x: number; y: number } | null>(null);
  const panelRef         = useRef<HTMLDivElement>(null);
  const loadRequestRef   = useRef(0);
  const savedCtrlRef     = useRef<SavedControls | null>(null);

  // Erase brush internals
  const isPaintingRef       = useRef(false);
  const currentStrokeSetRef = useRef(new Set<number>());
  const allMaskedSetRef     = useRef(new Set<number>());
  const pendingPaintRef     = useRef<{ x: number; y: number } | null>(null);
  const paintRafRef         = useRef<number | null>(null);
  const brushRadiusRef      = useRef(brushRadius);
  brushRadiusRef.current    = brushRadius; // keep in sync without re-creating RAF loop
  const cursorRef           = useRef<HTMLDivElement>(null);
  const lastScanPxRef       = useRef<{ x: number; y: number } | null>(null);
  // Spatial grid built once on erase-mode entry; null until ready.
  const splatGridRef        = useRef<SplatSpatialGrid | null>(null);
  // Throttle setSplatMask during drag — 4 GPU uploads/s max while brushing.
  const lastMaskUploadRef   = useRef(0);
  // Aerial erase: screen-space brush radius in pixels (scroll to adjust).
  const erasePixelRadiusRef   = useRef(isAerial ? 25 : 40);
  // Aerial erase: world-space elevation locked on pointer-down for the full stroke.
  const aerialLockedYRef      = useRef<number | null>(null);
  // Aerial erase: last stroke world position, used for interpolation between samples.
  const aerialLastWorldPosRef = useRef<THREE.Vector3 | null>(null);
  // Aerial erase: ref to the circle-cursor DOM element.
  const aerialCircleRef       = useRef<HTMLDivElement>(null);

  // Paint brush internals
  const isPaintingPaintRef    = useRef(false);
  const currentPaintMeshesRef = useRef<THREE.Mesh[]>([]);
  const pendingPaintPosRef    = useRef<{ x: number; y: number } | null>(null);
  const paintBrushRafRef      = useRef<number | null>(null);
  const paintBrushRadiusRef   = useRef(paintBrushRadius);
  paintBrushRadiusRef.current = paintBrushRadius;
  const paintColorRef         = useRef(paintColor);
  paintColorRef.current       = paintColor;
  const lastPaintPxRef        = useRef<{ x: number; y: number } | null>(null);
  const lastPaintWldRef       = useRef<THREE.Vector3 | null>(null);
  const paintCursorRef        = useRef<HTMLDivElement>(null);

  // Lasso tool internals
  const isDrawingLassoRef  = useRef(false);
  const lassoPointsRef     = useRef<{ x: number; y: number }[]>([]);
  const lassoCursorRef     = useRef<HTMLDivElement>(null);


  // Kept in sync in render body for use in async callbacks.
  const isActiveRef      = useRef(isActive);
  const activeAddIdRef   = useRef<string | null>(activeAddId);
  isActiveRef.current    = isActive;
  activeAddIdRef.current = activeAddId;
  availableAssetsRef.current = availableAssets;

  useEffect(() => {
    if (!isActive || !initialAssetId) return;
    // Only act when initialAssetId actually changes to a new value — avoids
    // spurious re-fires caused by availableAssets changing while the same
    // initialAssetId is still in effect (which can reset selectedId back to a
    // previously-selected asset mid-session).
    if (initialAssetId === lastActedInitialAssetIdRef.current) return;
    if (!availableAssetsRef.current.some(asset => asset.id === initialAssetId)) return;
    lastActedInitialAssetIdRef.current = initialAssetId;
    setMode('add');
    setSelectedId(initialAssetId);
    setAddStep('capturing');
  }, [initialAssetId, isActive]);

  // ── Stable control freeze / restore ──────────────────────────────────────
  // Must be defined before any useEffect that references them.
  const restoreControls = useCallback(() => {
    const saved = savedCtrlRef.current;
    if (!saved) return;
    const ctrl = saved.controls as Record<string, unknown>;
    if (typeof ctrl?.movementSpeed === 'number') ctrl.movementSpeed = saved.movementSpeed;
    if (typeof ctrl?.lookSpeed     === 'number') ctrl.lookSpeed     = saved.lookSpeed;
    savedCtrlRef.current = null;
  }, []);

  const freezeControls = useCallback(() => {
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const ctrl   = viewer?.controls as Record<string, unknown> | undefined;
    if (!ctrl || savedCtrlRef.current) return;
    savedCtrlRef.current = {
      controls:      ctrl,
      movementSpeed: (ctrl.movementSpeed as number) ?? 0,
      lookSpeed:     (ctrl.lookSpeed     as number) ?? 0,
    };
    if (typeof ctrl.movementSpeed === 'number') ctrl.movementSpeed = 0;
    if (typeof ctrl.lookSpeed     === 'number') ctrl.lookSpeed     = 0;
  }, [viewerRef]);

  const cancelDrag = useCallback(() => {
    if (!activeDragRef.current) return;
    activeDragRef.current = null;
    if (moveRafRef.current !== null) { cancelAnimationFrame(moveRafRef.current); moveRafRef.current = null; }
    restoreControls();
  }, [restoreControls]);

  // Reset applied-region tracker when eraseRegion prop changes or tool closes.
  useEffect(() => {
    appliedEraseRegionRef.current = null;
  }, [eraseRegion, isActive]);

  // Agent-triggered erase mode — fires when eraseModeToken increments.
  const lastEraseModeTokenRef = useRef(0);
  useEffect(() => {
    if (!isActive || !eraseModeToken || eraseModeToken === lastEraseModeTokenRef.current) return;
    lastEraseModeTokenRef.current = eraseModeToken;
    cancelDrag();
    setMode('erase');
  }, [isActive, eraseModeToken, cancelDrag]);

  // Apply eraseRegion: switch to erase mode and pre-select splats inside the sphere.
  useEffect(() => {
    if (!isActive || !eraseRegion) return;
    if (appliedEraseRegionRef.current === eraseRegion) return;
    appliedEraseRegionRef.current = eraseRegion;

    // Switch to erase mode.
    cancelDrag();
    setMode('erase');

    // Pre-populate erase zones from the sphere.
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const splatMesh = viewer?.splatMesh as SplatMesh | undefined;
    if (!splatMesh) return;
    const indices = scanSplatsInSphere(splatMesh, eraseRegion.center, eraseRegion.radius);
    const zone: EraseZone = { id: genId(), indices };
    eraseRef.current = [...eraseRef.current, zone];
    for (const idx of indices) allMaskedSetRef.current.add(idx);
    setEraseZones([...eraseRef.current]);
    rebuildMask(splatMesh, eraseRef.current);
  }, [isActive, eraseRegion, cancelDrag, viewerRef]);

  // ── AI-region sphere indicator ────────────────────────────────────────────
  useEffect(() => {
    if (!isActive || !eraseRegion) return;
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const scene  = viewer?.scene as THREE.Scene | undefined;
    if (!scene) return;
    const geo    = new THREE.SphereGeometry(eraseRegion.radius, 24, 16);
    const mat    = new THREE.MeshBasicMaterial({ color: 0xff3333, transparent: true, opacity: 0.18, side: THREE.FrontSide, depthTest: false });
    const sphere = new THREE.Mesh(geo, mat);
    sphere.position.copy(eraseRegion.center);
    sphere.renderOrder = 4;
    scene.add(sphere);
    return () => {
      scene.remove(sphere);
      geo.dispose();
      mat.dispose();
    };
  }, [isActive, eraseRegion, viewerRef]);

  // Box wireframe preview — show the LATEST box while in erase mode.
  const latestEraseBox = eraseBoxes?.length ? eraseBoxes[eraseBoxes.length - 1] : undefined;
  useEffect(() => {
    if (!isActive || !latestEraseBox || mode !== 'erase') return;
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const scene  = viewer?.scene as THREE.Scene | undefined;
    if (!scene) return;
    const { min, max } = latestEraseBox;
    const dx = max.x - min.x, dy = max.y - min.y, dz = max.z - min.z;
    const cx = (min.x + max.x) / 2, cy = (min.y + max.y) / 2, cz = (min.z + max.z) / 2;
    const geo      = new THREE.BoxGeometry(dx, dy, dz);
    const fillMat  = new THREE.MeshBasicMaterial({ color: 0xff3333, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthTest: false });
    const fill     = new THREE.Mesh(geo, fillMat);
    fill.position.set(cx, cy, cz);
    fill.renderOrder = 4;
    const edgesGeo = new THREE.EdgesGeometry(geo);
    const edgeMat  = new THREE.LineBasicMaterial({ color: 0xff4444 });
    const edges    = new THREE.LineSegments(edgesGeo, edgeMat);
    edges.position.set(cx, cy, cz);
    edges.renderOrder = 5;
    scene.add(fill, edges);
    return () => {
      scene.remove(fill, edges);
      geo.dispose(); fillMat.dispose(); edgesGeo.dispose(); edgeMat.dispose();
    };
  }, [isActive, latestEraseBox, mode, viewerRef]);

  // Apply eraseBoxes: mask new entries by ID — never clears previous masks.
  useEffect(() => {
    if (!isActive || !eraseBoxes?.length) return;
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const splatMesh = viewer?.splatMesh as SplatMesh | undefined;
    if (!splatMesh) return;
    let changed = false;
    for (const box of eraseBoxes) {
      if (appliedEraseBoxIdsRef.current.has(box.id)) continue;
      appliedEraseBoxIdsRef.current.add(box.id);
      const indices = scanSplatsInBox(splatMesh, box.min, box.max);
      const zone: EraseZone = { id: genId(), indices };
      eraseRef.current = [...eraseRef.current, zone];
      for (const idx of indices) allMaskedSetRef.current.add(idx);
      changed = true;
    }
    if (changed) {
      setEraseZones([...eraseRef.current]);
      rebuildMask(splatMesh, eraseRef.current);
    }
  }, [isActive, eraseBoxes, viewerRef]);

  // Apply agentPlacement: place a catalogue model (or procedural trench) scaled to targetBounds.
  useEffect(() => {
    if (!isActive || !agentPlacement) return;
    if (appliedAgentPlacementRef.current === agentPlacement) return;
    appliedAgentPlacementRef.current = agentPlacement;

    const viewer = viewerRef.current as Record<string, unknown> | null;
    if (!viewer) return;
    const viewerScene = (viewer.threeScene ?? viewer.scene) as THREE.Scene | undefined;
    const camera      = viewer.camera as THREE.Camera | undefined;
    if (!viewerScene || !camera) return;

    const doPlace = (model: THREE.Object3D, icon: string) => {
      const localBox = new THREE.Box3().setFromObject(model);
      const rawSize  = new THREE.Vector3();
      localBox.getSize(rawSize);

      let scale    = isAerial ? 6 : 1;
      let position = new THREE.Vector3(0, 0, 0);

      if (agentPlacement.targetBounds) {
        // Agent-specified bounds always win — override the aerial default.
        const { min, max } = agentPlacement.targetBounds;
        const bx = max.x - min.x;
        const bz = max.z - min.z;
        scale = Math.min(bx / Math.max(rawSize.x, 0.001), bz / Math.max(rawSize.z, 0.001));
        position.set((min.x + max.x) / 2, min.y - localBox.min.y * scale, (min.z + max.z) / 2);
      } else if (agentPlacement.dimensions) {
        // Claude computed appropriate furniture dimensions — scale to match, place in front of camera.
        // Match the largest suggested dimension against the largest raw axis so GLB orientation
        // doesn't matter (Trellis models have no guaranteed X/Z convention).
        const { width, length } = agentPlacement.dimensions;
        const maxSuggested = Math.max(width ?? 0, length ?? 0);
        const maxRaw = Math.max(rawSize.x, rawSize.z);
        if (maxSuggested > 0 && maxRaw > 0.001) {
          scale = maxSuggested / maxRaw;
        }
        console.log('[EditSceneTool] dimensions scale:', { suggestedDimensions: agentPlacement.dimensions, rawSize: { x: rawSize.x, y: rawSize.y, z: rawSize.z }, maxSuggested, maxRaw, scale });
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        forward.y = 0;
        if (forward.lengthSq() > 0.0001) forward.normalize();
        position.copy(camera.position).addScaledVector(forward, 2);
        position.y = camera.position.y - localBox.min.y * scale;
      }

      const meshMaterials: PlacedObject['meshMaterials'] = [];
      model.traverse(node => {
        if (!(node instanceof THREE.Mesh)) return;
        meshMaterials.push({ mesh: node, unlit: node.material, pbr: node.material });
      });

      const root = new THREE.Object3D();
      root.add(model);
      root.renderOrder = 2;
      root.scale.setScalar(scale);
      root.position.copy(position);

      const boxHelper = new THREE.BoxHelper(root, 0xFDBA59);
      (boxHelper.material as THREE.LineBasicMaterial).depthTest = false;
      boxHelper.renderOrder = 998;

      const handles = makeHandles(root, camera, viewerScene);
      viewerScene.add(root, boxHelper);
      root.updateMatrixWorld(true);

      const placed: PlacedObject = {
        id: genId(), root, model, boxHelper, handles, rawSize,
        localMinY: localBox.min.y, surfaceY: position.y,
        scale, rotationY: 0,
        label: agentPlacement.label, icon,
        scene: viewerScene, shadingMode: 'pbr', meshMaterials,
      };
      placedRef.current = [...placedRef.current, placed];
      setPlacedObjects([...placedRef.current]);
      setActiveAddId(placed.id);
      setMode('add');
      setAddStep('interactive');
      onAgentPlacementDone?.();
    };

    // Trench: build procedurally from the selection bounds — no GLB needed.
    if (agentPlacement.catalogueId === 'trench' && agentPlacement.targetBounds) {
      const { min, max } = agentPlacement.targetBounds;
      const dims = agentPlacement.dimensions;
      // For click-mode selections worldBounds extents are ~0; enforce 2 m minimum for length.
      const trenchWidth  = dims?.width  ?? Math.max(max.x - min.x, 0.5);
      const trenchDepth  = dims?.depth  ?? 1.5;
      const trenchLength = dims?.length ?? Math.max(max.z - min.z, 2.0);

      // Open U-channel: floor + two long walls + two end walls.
      // Root sits ON the road surface (max.y); user presses HEIGHT ▼ to sink it underground.
      const concreteMat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.9, metalness: 0 });
      const floorGeo    = new THREE.BoxGeometry(trenchWidth, 0.1, trenchLength);
      const wallGeo     = new THREE.BoxGeometry(0.15, trenchDepth, trenchLength);
      const endGeo      = new THREE.BoxGeometry(Math.max(trenchWidth - 0.3, 0.05), trenchDepth, 0.15);

      const floor  = new THREE.Mesh(floorGeo, concreteMat);
      const wallL  = new THREE.Mesh(wallGeo,  concreteMat);
      const wallR  = new THREE.Mesh(wallGeo,  concreteMat);
      const endF   = new THREE.Mesh(endGeo,   concreteMat);
      const endB   = new THREE.Mesh(endGeo,   concreteMat);

      floor.position.set(0, 0.05, 0);
      wallL.position.set(-trenchWidth / 2 + 0.075, trenchDepth / 2, 0);
      wallR.position.set( trenchWidth / 2 - 0.075, trenchDepth / 2, 0);
      endF.position.set(0, trenchDepth / 2, -trenchLength / 2 + 0.075);
      endB.position.set(0, trenchDepth / 2,  trenchLength / 2 - 0.075);

      const trenchGroup = new THREE.Group();
      trenchGroup.add(floor, wallL, wallR, endF, endB);

      // max.y = road surface (worldCenter.y for click mode, or top of worldBounds for box mode).
      const position = new THREE.Vector3(
        (min.x + max.x) / 2,
        max.y,
        (min.z + max.z) / 2,
      );

      const meshMaterials: PlacedObject['meshMaterials'] = [];
      trenchGroup.traverse(node => {
        if (!(node instanceof THREE.Mesh)) return;
        meshMaterials.push({ mesh: node, unlit: node.material, pbr: node.material });
      });

      const root = new THREE.Object3D();
      root.add(trenchGroup);
      root.renderOrder = 2;
      root.position.copy(position);

      const rawSize = new THREE.Vector3(trenchWidth, trenchDepth, trenchLength);
      const boxHelper = new THREE.BoxHelper(root, 0xFDBA59);
      (boxHelper.material as THREE.LineBasicMaterial).depthTest = false;
      boxHelper.renderOrder = 998;

      const handles = makeHandles(root, camera, viewerScene);
      viewerScene.add(root, boxHelper);
      root.updateMatrixWorld(true);

      const placed: PlacedObject = {
        id: genId(), root, model: trenchGroup, boxHelper, handles, rawSize,
        localMinY: 0, surfaceY: position.y,
        scale: 1, rotationY: 0,
        label: agentPlacement.label, icon: '🪏',
        scene: viewerScene, shadingMode: 'pbr', meshMaterials,
      };
      placedRef.current = [...placedRef.current, placed];
      setPlacedObjects([...placedRef.current]);
      setActiveAddId(placed.id);
      setMode('add');
      setAddStep('interactive');
      onAgentPlacementDone?.();
      return;
    }

    // Trellis GLB: load directly from the provided URL.
    if (agentPlacement.glbUrl) {
      const requestId = ++loadRequestRef.current;
      setAddStep('capturing');
      loader.load(agentPlacement.glbUrl, gltf => {
        if (loadRequestRef.current !== requestId) return;
        doPlace(gltf.scene, '✨');
      }, undefined, err => {
        console.error('[EditSceneTool] agentPlacement glbUrl load error:', err);
        if (loadRequestRef.current === requestId) setAddStep('selecting');
      });
      return;
    }

    // All other catalogue models: load GLB and scale to fit.
    const cat = availableAssetsRef.current.find(a => a.id === agentPlacement.catalogueId);
    if (!cat) { setAddStep('selecting'); return; }

    const requestId = ++loadRequestRef.current;
    setAddStep('capturing');
    loader.load(cat.url, gltf => {
      if (loadRequestRef.current !== requestId) return;
      doPlace(gltf.scene, cat.icon ?? '✨');
    }, undefined, err => {
      console.error('[EditSceneTool] agentPlacement load error:', err);
      if (loadRequestRef.current === requestId) setAddStep('selecting');
    });
  }, [isActive, agentPlacement, viewerRef, onAgentPlacementDone]);

  // Reset agentPlacement tracker on prop change or close.
  useEffect(() => {
    appliedAgentPlacementRef.current = null;
  }, [agentPlacement, isActive]);

  // ── Lights ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current as Record<string, unknown> | null;
    if (!viewer?.scene) return;
    const scene   = viewer.scene as THREE.Scene;
    const ambient = new THREE.AmbientLight(0xffffff, 1.2);
    const sun     = new THREE.DirectionalLight(0xffffff, 1.5);
    sun.position.set(1, 3, 2);
    scene.add(ambient, sun);
    return () => { scene.remove(ambient, sun); };
  }, [viewerRef]);

  // ── Match renderer clear colour to scene sky when in erase mode ──────────
  useEffect(() => {
    if (mode !== 'erase' || !isActive) return;
    const viewer   = viewerRef.current as Record<string, unknown> | null;
    const renderer = viewer?.renderer as THREE.WebGLRenderer | undefined;
    if (!renderer) return;

    const origColor = new THREE.Color();
    renderer.getClearColor(origColor);
    const origAlpha = renderer.getClearAlpha();
    renderer.setClearColor(0x8bb8d4, 1);

    let cancelled = false;
    (async () => {
      try {
        const capture = viewer?.captureFrame;
        if (typeof capture !== 'function') return;
        const dataUrl: string = (capture as (w: number) => string).call(viewer, 128);
        if (!dataUrl?.startsWith('data:image')) return;
        const sky = await sampleSkyColor(dataUrl);
        if (!cancelled) renderer.setClearColor(sky, 1);
      } catch { /* keep neutral sky blue */ }
    })();

    return () => {
      cancelled = true;
      renderer.setClearColor(origColor, origAlpha);
    };
  }, [mode, isActive, viewerRef]);

  // Build the spatial grid asynchronously when entering erase mode — the
  // synchronous SplatSpatialGrid constructor scans every splat and can block
  // the main thread for several seconds on large aerial scenes.  Yielding with
  // setTimeout lets the browser paint a "Preparing eraser…" hint first.
  useEffect(() => {
    if (!isActive || mode !== 'erase') return;
    const viewer    = viewerRef.current as Record<string, unknown> | null;
    const splatMesh = viewer?.splatMesh as SplatMesh | undefined;
    if (!splatMesh || splatGridRef.current) return;

    setIsBuildingGrid(true);
    const tid = setTimeout(() => {
      splatGridRef.current = new SplatSpatialGrid(splatMesh);
      setIsBuildingGrid(false);
    }, 0);
    return () => clearTimeout(tid);
  }, [isActive, mode, viewerRef]);

  // ── Erase RAF loop — runs while erase mode is active ────────────────────
  // Aerial path: locked-plane intersection + two-ray world-radius + ~10 fps preview.
  // Non-aerial path: splat BVH pick + 250 ms throttled mask upload.
  useEffect(() => {
    if (!isActive || mode !== 'erase') return;

    const loop = () => {
      paintRafRef.current = requestAnimationFrame(loop);
      const pos = pendingPaintRef.current;
      if (!pos || !isPaintingRef.current) return;

      const last = lastScanPxRef.current;
      if (last && Math.abs(pos.x - last.x) < 5 && Math.abs(pos.y - last.y) < 5) return;

      pendingPaintRef.current = null;
      lastScanPxRef.current   = { x: pos.x, y: pos.y };

      const v  = viewerRef.current as Record<string, unknown> | null;
      const sm = v?.splatMesh as SplatMesh | undefined;
      if (!sm) return;

      if (isAerial) {
        // ── Aerial: locked-plane intersection, two-ray world radius, ~10 fps preview ──
        const camera   = v?.camera   as THREE.PerspectiveCamera | undefined;
        const renderer = v?.renderer as THREE.WebGLRenderer      | undefined;
        if (!camera || !renderer) return;

        const lockedY = aerialLockedYRef.current ?? (camera.position.y - 50);
        const rect    = renderer.domElement.getBoundingClientRect();
        const plane   = new THREE.Plane(new THREE.Vector3(0, 1, 0), -lockedY);

        const rc = new THREE.Raycaster();
        rc.setFromCamera(
          new THREE.Vector2(
            ((pos.x - rect.left) / rect.width)  *  2 - 1,
            ((pos.y - rect.top)  / rect.height) * -2 + 1,
          ),
          camera,
        );
        const planePt = new THREE.Vector3();
        if (!rc.ray.intersectPlane(plane, planePt)) return;

        // Exact world radius: intersect a second ray pixelRadius px to the right and
        // measure the plane-space gap.  Handles oblique cameras and wide-angle edges
        // correctly — the displayed circle and erased footprint will always agree.
        const rcRight = new THREE.Raycaster();
        rcRight.setFromCamera(
          new THREE.Vector2(
            ((pos.x - rect.left + erasePixelRadiusRef.current) / rect.width) * 2 - 1,
            ((pos.y - rect.top)  / rect.height) * -2 + 1,
          ),
          camera,
        );
        const rightPt = new THREE.Vector3();
        let worldRadius: number;
        if (rcRight.ray.intersectPlane(plane, rightPt)) {
          worldRadius = planePt.distanceTo(rightPt);
        } else {
          // Fallback: depth approximation (plane nearly parallel to view ray)
          const d = camera.position.distanceTo(planePt);
          worldRadius = erasePixelRadiusRef.current * 2 * d
            * Math.tan(((camera.fov ?? 60) * Math.PI / 180) / 2)
            / (renderer.domElement.clientHeight || 1);
        }

        // Interpolate from the previous stroke position to fill path gaps.
        const lastPos  = aerialLastWorldPosRef.current;
        const toVisit: THREE.Vector3[] = [];
        if (lastPos) {
          const dist  = lastPos.distanceTo(planePt);
          const steps = Math.max(1, Math.ceil(dist / (worldRadius * 0.5)));
          for (let i = 0; i < steps; i++) {
            toVisit.push(lastPos.clone().lerp(planePt, i / steps));
          }
        }
        toVisit.push(planePt.clone());
        aerialLastWorldPosRef.current = planePt.clone();

        const grid = splatGridRef.current;
        for (const wpos of toVisit) {
          const idxs = grid
            ? grid.queryRadius(wpos.x, wpos.y, wpos.z, worldRadius)
            : scanSplatsInSphere(sm, wpos, worldRadius);
          for (const idx of idxs) {
            if (!currentStrokeSetRef.current.has(idx) && !allMaskedSetRef.current.has(idx)) {
              currentStrokeSetRef.current.add(idx);
            }
          }
        }
        // ~10 fps preview during drag so the scene gives live feedback;
        // pointer-up always flushes the final complete set.
        const now = performance.now();
        if (now - lastMaskUploadRef.current >= 100) {
          lastMaskUploadRef.current = now;
          const combined = new Set([...allMaskedSetRef.current, ...currentStrokeSetRef.current]);
          sm.setSplatMask(new Uint32Array([...combined]));
        }
        return;
      }

      // ── Non-aerial: splat BVH pick + throttled mask upload ──────────────────
      const pt = pickWorldPointFromPointer(viewerRef.current, pos.x, pos.y);
      if (!pt) return;

      const grid    = splatGridRef.current;
      const newIdxs = grid
        ? grid.queryRadius(pt.x, pt.y, pt.z, brushRadiusRef.current)
        : scanSplatsInSphere(sm, new THREE.Vector3(pt.x, pt.y, pt.z), brushRadiusRef.current);

      let added = 0;
      for (const idx of newIdxs) {
        if (!currentStrokeSetRef.current.has(idx) && !allMaskedSetRef.current.has(idx)) {
          currentStrokeSetRef.current.add(idx);
          added++;
        }
      }

      if (added > 0) {
        const now = performance.now();
        if (now - lastMaskUploadRef.current >= 250) {
          lastMaskUploadRef.current = now;
          const combined = new Set([...allMaskedSetRef.current, ...currentStrokeSetRef.current]);
          sm.setSplatMask(new Uint32Array([...combined]));
        }
      }
    };
    paintRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (paintRafRef.current !== null) { cancelAnimationFrame(paintRafRef.current); paintRafRef.current = null; }
    };
  }, [isActive, mode, viewerRef, isAerial]); // brushRadius/erasePixelRadius read via ref

  // Drop the cached grid when leaving erase mode or closing the tool.
  useEffect(() => {
    if (!isActive || mode !== 'erase') {
      splatGridRef.current = null;
      setIsBuildingGrid(false);
      if (aerialCircleRef.current) aerialCircleRef.current.style.display = 'none';
    }
  }, [isActive, mode]);

  // ── Paint RAF loop — places colored circle meshes on splat surface ────────
  useEffect(() => {
    if (!isActive || mode !== 'paint') return;
    const loop = () => {
      paintBrushRafRef.current = requestAnimationFrame(loop);
      const pos = pendingPaintPosRef.current;
      if (!pos || !isPaintingPaintRef.current) return;

      const last = lastPaintPxRef.current;
      if (last && Math.abs(pos.x - last.x) < 4 && Math.abs(pos.y - last.y) < 4) return;

      pendingPaintPosRef.current = null;
      lastPaintPxRef.current     = { x: pos.x, y: pos.y };

      const viewer = viewerRef.current as Record<string, unknown> | null;
      const scene  = viewer?.scene as THREE.Scene | undefined;
      if (!scene) return;

      let pt = pickWorldPointFromPointer(viewerRef.current, pos.x, pos.y);
      // Fallback to last known world point so painting continues over void gaps.
      if (!pt && lastPaintWldRef.current) {
        pt = { x: lastPaintWldRef.current.x, y: lastPaintWldRef.current.y, z: lastPaintWldRef.current.z };
      }
      if (!pt) return;
      lastPaintWldRef.current = new THREE.Vector3(pt.x, pt.y, pt.z);

      const r    = paintBrushRadiusRef.current;
      const geom = new THREE.CircleGeometry(r, 20);
      const mat  = new THREE.MeshBasicMaterial({
        color:       paintColorRef.current,
        depthWrite:  false,
        transparent: true,
        opacity:     0.88,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(pt.x, pt.y + 0.02, pt.z);
      mesh.renderOrder = 5;
      scene.add(mesh);
      currentPaintMeshesRef.current.push(mesh);
    };
    paintBrushRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (paintBrushRafRef.current !== null) {
        cancelAnimationFrame(paintBrushRafRef.current);
        paintBrushRafRef.current = null;
      }
    };
  }, [isActive, mode, viewerRef]);

  // ── Full cleanup on unmount ────────────────────────────────────────────────
  useEffect(() => () => {
    ++loadRequestRef.current;
    if (paintRafRef.current     !== null) { cancelAnimationFrame(paintRafRef.current);      paintRafRef.current     = null; }
    if (paintBrushRafRef.current !== null) { cancelAnimationFrame(paintBrushRafRef.current); paintBrushRafRef.current = null; }
    cancelDrag();
    for (const p of placedRef.current) disposePlacedObject(p);
    placedRef.current = [];
    eraseRef.current  = [];
    appliedEraseBoxIdsRef.current.clear();
    currentStrokeSetRef.current.clear();
    allMaskedSetRef.current.clear();
    const sm = (viewerRef.current as Record<string, unknown>)?.splatMesh as SplatMesh | undefined;
    sm?.clearSplatMask();
    // Dispose all paint meshes.
    for (const z of paintZonesRef.current) {
      for (const m of z.meshes) {
        z.scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    }
    paintZonesRef.current = [];
    lassoPointsRef.current  = [];
  }, [cancelDrag, viewerRef]);

  // ── Invalidate loads and cancel drag on close ─────────────────────────────
  useEffect(() => {
    if (!isActive) {
      ++loadRequestRef.current;
      cancelDrag();
      // Hide selection UI for all placed objects when tool closes
      for (const p of placedRef.current) {
        p.boxHelper.visible = false;
        p.handles.forEach(h => { h.visible = false; });
      }
      setActiveAddId(null);
    }
  }, [isActive, cancelDrag]);

  // ── Block native events on panel ──────────────────────────────────────────
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const stop = (e: Event) => e.stopPropagation();
    panel.addEventListener('pointerdown', stop, { capture: true });
    panel.addEventListener('pointermove', stop, { capture: true });
    panel.addEventListener('pointerup',   stop, { capture: true });
    panel.addEventListener('wheel',       stop, { capture: true });
    return () => {
      panel.removeEventListener('pointerdown', stop, { capture: true });
      panel.removeEventListener('pointermove', stop, { capture: true });
      panel.removeEventListener('pointerup',   stop, { capture: true });
      panel.removeEventListener('wheel',       stop, { capture: true });
    };
  }, [isActive]);

  // ── Sync add-object highlight + handle visibility ─────────────────────────
  useEffect(() => {
    placedRef.current.forEach(p => {
      const isSel = p.id === activeAddId;
      p.boxHelper.visible = isSel;
      p.handles.forEach(h => { h.visible = isSel; });
    });
  }, [activeAddId, placedObjects]);

  // ── Restore visibility on reopen; re-apply splat mask ─────────────────────
  useEffect(() => {
    if (!isActive) return;
    const curAddId = activeAddIdRef.current;
    for (const p of placedRef.current) {
      p.boxHelper.visible = p.id === curAddId;
      p.handles.forEach(h => { h.visible = p.id === curAddId; });
    }
    // Re-apply the mask in case it was cleared while the tool was closed.
    if (allMaskedSetRef.current.size > 0) {
      const sm = (viewerRef.current as Record<string, unknown>)?.splatMesh as SplatMesh | undefined;
      sm?.setSplatMask(new Uint32Array([...allMaskedSetRef.current]));
    }
    // When a Trellis asset is pre-selected, the initialAssetId effect (defined earlier)
    // already fired and set addStep → 'capturing'. Don't overwrite it.
    if (initialAssetId && availableAssetsRef.current.some(a => a.id === initialAssetId)) return;
    setAddStep(placedRef.current.length > 0 ? 'interactive' : 'selecting');
  }, [isActive, viewerRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reopen the toolbox with the named placed object active. Ignore filename
  // extensions and punctuation so chat names such as "stop sign" and
  // "stopsign.glb" both match an uploaded asset labelled "stopsign".
  useEffect(() => {
    if (!isActive || !editAssetLabel) return;
    const normalize = (value: string) => value
      .toLowerCase()
      .replace(/\.(?:glb|gltf)$/i, '')
      .replace(/[^a-z0-9]+/g, '');
    const target = normalize(editAssetLabel);
    const match = placedRef.current.find(object => normalize(object.label) === target)
      ?? placedRef.current.find(object => normalize(object.label).includes(target) || target.includes(normalize(object.label)));
    if (!match) return;
    setMode('add');
    setAddStep('interactive');
    setActiveAddId(match.id);

    const viewer = viewerRef.current as {
      camera?: THREE.Camera & { position: THREE.Vector3 };
      teleportTo?: (target: { position?: number[] }) => Promise<void>;
      controls?: { updateLookDirection?: () => void };
      forceRenderNextFrame?: () => void;
    } | null;
    const camera = viewer?.camera;
    if (!viewer || !camera?.position) return;

    const bounds = new THREE.Box3().setFromObject(match.model);
    if (bounds.isEmpty()) return;
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const distance = Math.max(size.length() * 1.6, 3);
    const direction = camera.position.clone().sub(center);
    direction.y = Math.max(direction.y, size.y * 0.35);
    if (direction.lengthSq() < 0.001) direction.set(1, 0.4, 1);
    direction.normalize();
    const cameraTarget = center.clone().addScaledVector(direction, distance);

    const focusObject = async () => {
      if (viewer.teleportTo) await viewer.teleportTo({ position: cameraTarget.toArray() });
      else camera.position.copy(cameraTarget);
      camera.lookAt(center);
      camera.updateMatrixWorld(true);
      viewer.controls?.updateLookDirection?.();
      viewer.forceRenderNextFrame?.();
    };
    void focusObject();
  }, [editAssetLabel, isActive, viewerRef]);

  // ── RAF loop: camera-aware handle scale ───────────────────────────────────
  useEffect(() => {
    if (!isActive || mode !== 'add' || addStep !== 'interactive' || !activeAddId) return;
    let raf: number;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const obj    = placedRef.current.find(p => p.id === activeAddId);
      const viewer = viewerRef.current as Record<string, unknown> | null;
      const cam    = viewer?.camera as THREE.Camera | undefined;
      if (obj && cam) rescaleHandles(obj.handles, obj.root, cam);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isActive, mode, addStep, activeAddId, viewerRef]);

  // ── Raycasting (add mode) ─────────────────────────────────────────────────
  const getRaycaster = useCallback((cx: number, cy: number) => {
    const viewer = viewerRef.current as Record<string, unknown> | null;
    if (!viewer?.camera || !viewer?.renderer) return null;
    const renderer = viewer.renderer as THREE.WebGLRenderer;
    const camera   = viewer.camera   as THREE.Camera;
    const rect = renderer.domElement.getBoundingClientRect();
    const rc   = new THREE.Raycaster();
    rc.setFromCamera(
      new THREE.Vector2(
        ((cx - rect.left) / rect.width)  *  2 - 1,
        ((cy - rect.top)  / rect.height) * -2 + 1,
      ),
      camera,
    );
    return rc;
  }, [viewerRef]);

  const raycastAddScene = useCallback((cx: number, cy: number) => {
    const rc = getRaycaster(cx, cy);
    if (!rc) return null;
    const placed    = placedRef.current;
    const activeObj = placed.find(p => p.id === activeAddId);
    if (activeObj) {
      const hits = rc.intersectObjects(activeObj.handles.filter(h => h.visible), false);
      if (hits.length > 0)
        return { type: 'handle' as const, objectId: activeObj.id, point: hits[0].point };
    }
    type HE = { mesh: THREE.Object3D; objectId: string };
    const indexed: HE[] = [];
    placed.forEach(p => p.model.traverse(c => {
      if ((c as THREE.Mesh).isMesh) indexed.push({ mesh: c, objectId: p.id });
    }));
    const oHits = rc.intersectObjects(indexed.map(g => g.mesh), false);
    if (oHits.length > 0) {
      const g = indexed.find(g => g.mesh === oHits[0].object);
      if (g) return { type: 'object' as const, objectId: g.objectId, point: oHits[0].point };
    }
    return null;
  }, [getRaycaster, activeAddId]);

  // ── Document-level drag listeners (add mode) ──────────────────────────────
  useEffect(() => {
    if (!isActive || mode !== 'add' || addStep !== 'interactive') return;

    const onDown = (e: PointerEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      const hit = raycastAddScene(e.clientX, e.clientY);
      if (!hit) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      const obj = placedRef.current.find(p => p.id === hit.objectId);
      if (!obj) return;
      setActiveAddId(hit.objectId);
      freezeControls();
      if (hit.type === 'object') {
        activeDragRef.current = {
          type: 'move', objectId: hit.objectId,
          xzOffset: { x: hit.point.x - obj.root.position.x, z: hit.point.z - obj.root.position.z },
        };
      } else {
        activeDragRef.current = {
          type: 'resize', objectId: hit.objectId,
          startClientY: e.clientY, startScale: obj.scale,
        };
      }
    };

    const onMove = (e: PointerEvent) => {
      const drag = activeDragRef.current;
      if (!drag || panelRef.current?.contains(e.target as Node)) return;
      e.stopPropagation();
      const obj = placedRef.current.find(p => p.id === drag.objectId);
      if (!obj) return;
      if (drag.type === 'resize') {
        const ratio    = Math.exp((drag.startClientY! - e.clientY) / 250);
        const newScale = Math.max(0.01, drag.startScale! * ratio);
        obj.root.scale.setScalar(newScale);
        obj.scale = newScale;
        obj.root.position.y = obj.surfaceY - obj.localMinY * newScale;
        obj.boxHelper.update();
        repositionHandles(obj.handles, obj.root);
        return;
      }
      const cx = e.clientX, cy = e.clientY;
      if (moveRafRef.current !== null) cancelAnimationFrame(moveRafRef.current);
      moveRafRef.current = requestAnimationFrame(() => {
        moveRafRef.current = null;
        const d = activeDragRef.current;
        if (!d || d.type !== 'move') return;
        const o = placedRef.current.find(p => p.id === d.objectId);
        if (!o) return;
        const surfPt = pickWorldPointFromPointer(viewerRef.current, cx, cy);
        let wx: number, wy: number, wz: number;
        if (surfPt) {
          o.surfaceY = surfPt.y;
          wx = surfPt.x; wy = surfPt.y; wz = surfPt.z;
        } else {
          // Splat BVH missed (cursor over placed model or sparse area).
          // Fall back to horizontal plane at the object's last known surface Y.
          const rc2 = getRaycaster(cx, cy);
          if (!rc2) return;
          const planePt = new THREE.Vector3();
          const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -o.surfaceY);
          if (!rc2.ray.intersectPlane(plane, planePt)) return;
          wx = planePt.x; wy = o.surfaceY; wz = planePt.z;
        }
        o.root.position.set(
          wx - d.xzOffset!.x,
          wy - o.localMinY * o.scale,
          wz - d.xzOffset!.z,
        );
        o.boxHelper.update();
        repositionHandles(o.handles, o.root);
      });
    };

    const onUp = (e: PointerEvent) => {
      const drag = activeDragRef.current;
      if (!drag) return;
      e.stopPropagation();
      const obj = placedRef.current.find(p => p.id === drag.objectId);
      cancelDrag();
      if (drag.type === 'resize' && obj) {
        setPlacedObjects(prev =>
          prev.map(p => p.id === drag.objectId ? { ...p, scale: obj.scale, surfaceY: obj.surfaceY } : p),
        );
      }
    };

    document.addEventListener('pointerdown',   onDown,     { capture: true });
    document.addEventListener('pointermove',   onMove,     { capture: true });
    document.addEventListener('pointerup',     onUp,       { capture: true });
    document.addEventListener('pointercancel', cancelDrag, { capture: true });
    window  .addEventListener('blur',          cancelDrag);
    return () => {
      cancelDrag();
      document.removeEventListener('pointerdown',   onDown,     { capture: true });
      document.removeEventListener('pointermove',   onMove,     { capture: true });
      document.removeEventListener('pointerup',     onUp,       { capture: true });
      document.removeEventListener('pointercancel', cancelDrag, { capture: true });
      window  .removeEventListener('blur',          cancelDrag);
    };
  }, [isActive, mode, addStep, viewerRef, raycastAddScene, freezeControls, cancelDrag]);

  // ── Place GLB object ──────────────────────────────────────────────────────
  const handlePlaceDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
    placeDownRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePlaceUp = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
    const down = placeDownRef.current;
    placeDownRef.current = null;
    if (!down || Math.abs(e.clientX - down.x) > 6 || Math.abs(e.clientY - down.y) > 6) return;

    const surfPt = pickWorldPointFromPointer(viewerRef.current, e.clientX, e.clientY);
    if (!surfPt) { console.warn('[EditSceneTool] ray pick failed'); return; }

    const cat = availableAssets.find(o => o.id === selectedId);
    if (!cat) return;
    setAddStep('loading');

    const requestId      = ++loadRequestRef.current;
    const capturedViewer = viewerRef.current as Record<string, unknown>;

    const rendererScene = (capturedViewer.threeScene ?? capturedViewer.scene) as THREE.Scene | undefined;
    const sceneSupportsSplats = rendererScene?.children
      .some(child => child instanceof SparkRenderer);
    if (cat.format === 'splat' && sceneSupportsSplats) {
      console.info('[EditSceneTool] Placing Trellis asset as Gaussian PLY', cat.url);
      const splatMesh = new SplatMesh({ url: cat.url, extSplats: true, raycastable: false });
      void splatMesh.initialized.then(() => {
        if (
          loadRequestRef.current !== requestId ||
          !isActiveRef.current ||
          (viewerRef.current as unknown) !== (capturedViewer as unknown) ||
          !capturedViewer?.scene
        ) {
          splatMesh.dispose();
          return;
        }

        // TRELLIS exports PLY with the transpose of the axis transform used by
        // its GLB exporter. Their relative transform is 180 degrees around X.
        splatMesh.quaternion.set(1, 0, 0, 0);
        splatMesh.updateMatrix();
        const localBox = splatMesh.getQuantileBounds({ percentile: 0.98 }).box.clone().applyMatrix4(splatMesh.matrix);
        if (localBox.isEmpty()) throw new Error('Generated splat contains no visible points');
        const rawSize = localBox.getSize(new THREE.Vector3());
        const localCenter = localBox.getCenter(new THREE.Vector3());
        splatMesh.position.sub(localCenter);

        const model = new THREE.Group();
        model.add(splatMesh);
        // SplatMesh has no Three.js BufferGeometry bounds. This invisible proxy lets
        // the existing placement box and resize handles follow its generated bounds.
        const boundsProxy = new THREE.Mesh(
          new THREE.BoxGeometry(rawSize.x, rawSize.y, rawSize.z),
          new THREE.MeshBasicMaterial({ visible: false }),
        );
        model.add(boundsProxy);

        const localMinY = -rawSize.y / 2;
        // Use targetHeight if specified (e.g. LOS mode: sign=2.5m, car=1.8m).
        // Fall back to normalising the longest side to 1 unit so it appears furniture-sized.
        const initialScale = cat.targetHeight
          ? cat.targetHeight / Math.max(rawSize.y, 0.001)
          : (1 / Math.max(rawSize.x, rawSize.y, rawSize.z, 0.001)) * (isAerial ? 6 : 1);
        const root = new THREE.Object3D();
        root.add(model);
        root.scale.setScalar(initialScale);
        root.position.set(surfPt.x, surfPt.y - localMinY * initialScale, surfPt.z);

        const boxHelper = new THREE.BoxHelper(root, 0xFDBA59);
        (boxHelper.material as THREE.LineBasicMaterial).depthTest = false;
        boxHelper.renderOrder = 998;
        const scene = capturedViewer.scene as THREE.Scene;
        const camera = capturedViewer.camera as THREE.Camera;

        // Orient model to face camera's look direction (yaw only)
        const camFwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        camFwd.y = 0;
        const initialRotDeg = camFwd.lengthSq() > 0.0001
          ? Math.round(THREE.MathUtils.radToDeg(Math.atan2(camFwd.x, camFwd.z)) + 360) % 360
          : 0;
        root.rotation.y = THREE.MathUtils.degToRad(initialRotDeg);

        const handles = makeHandles(root, camera, scene);
        scene.add(root, boxHelper);
        onObjectPlaced?.(root, cat.id);

        const placed: PlacedObject = {
          id: genId(), root, model, boxHelper, handles, rawSize, localMinY,
          surfaceY: surfPt.y, scale: initialScale, rotationY: initialRotDeg,
          label: cat.label, icon: cat.icon ?? '✨', scene,
          shadingMode: 'unlit', meshMaterials: [], splatMesh,
        };
        placedRef.current = [...placedRef.current, placed];
        setPlacedObjects([...placedRef.current]);
        setActiveAddId(placed.id);
        setAddStep('interactive');
      }).catch(error => {
        splatMesh.dispose();
        console.error('[EditSceneTool] generated splat load error:', error);
        if (loadRequestRef.current === requestId) setAddStep('capturing');
      });
      return;
    }

    const modelUrl = cat.format === 'splat' ? cat.fallbackUrl ?? cat.url : cat.url;
    if (cat.format === 'splat') {
      console.info('[EditSceneTool] Spark renderer unavailable; placing Trellis GLB fallback', modelUrl);
    }
    loader.load(modelUrl, gltf => {
      if (
        loadRequestRef.current !== requestId  ||
        !isActiveRef.current                  ||
        (viewerRef.current as unknown) !== (capturedViewer as unknown) ||
        !capturedViewer?.scene
      ) {
        disposeModelGeometry(gltf.scene);
        return;
      }
      const model = gltf.scene;
      // Trellis GLBs have lighting baked into their textures — PBR shading
      // double-darkens them. Use MeshBasicMaterial to display the texture as-is.
      const useTrellisUnlit = cat.source === 'trellis';
      const meshMaterials: MeshMaterials[] = [];
      model.traverse(child => {
        child.renderOrder = 2;
        if ((child as THREE.Mesh).isMesh) {
          const mesh     = child as THREE.Mesh;
          const wasArray = Array.isArray(mesh.material);
          const origMats = wasArray
            ? mesh.material as THREE.Material[]
            : [mesh.material as THREE.Material];
          origMats.forEach(m => { m.depthWrite = true; m.depthTest = true; });
          if (useTrellisUnlit) {
            // Build unlit copies; keep origMats as the PBR set for future toggling.
            const unlitMats = origMats.map(m => {
              const pbr   = m as THREE.MeshStandardMaterial;
              const unlit = new THREE.MeshBasicMaterial({
                // Use the baseColorMap; fall back to emissiveMap in case Trellis baked
                // lighting there instead.  Always white color — baseColorFactor can be
                // (0,0,0) in Trellis GLBs, which would otherwise multiply the texture black.
                map:          pbr.map ?? pbr.emissiveMap ?? null,
                color:        new THREE.Color(0xffffff),
                vertexColors: pbr.vertexColors ?? false,
                transparent:  pbr.transparent,
                opacity:      pbr.opacity,
              });
              unlit.depthWrite = true;
              unlit.depthTest  = true;
              return unlit;
            });
            meshMaterials.push({
              mesh,
              unlit: wasArray ? unlitMats          : unlitMats[0],
              pbr:   wasArray ? origMats            : origMats[0],
            });
            mesh.material = wasArray ? unlitMats : unlitMats[0];
          } else {
            // Catalogue: unlit and pbr point to the same material set (no toggle needed).
            meshMaterials.push({ mesh, unlit: mesh.material, pbr: mesh.material });
          }
        }
      });
      const localBox  = new THREE.Box3().setFromObject(model);
      const localMinY = localBox.min.y;
      const rawSize   = new THREE.Vector3();
      localBox.getSize(rawSize);
      const aerialMul    = isAerial ? 6 : 1;
      const initialScale = cat.targetHeight
        ? (cat.targetHeight / Math.max(rawSize.y, 0.001)) * aerialMul
        : cat.source === 'trellis'
          ? (2 / Math.max(rawSize.x, rawSize.y, rawSize.z, 0.001)) * aerialMul
          : 1 * aerialMul;

      const root = new THREE.Object3D();
      root.add(model);
      root.renderOrder = 2;
      root.scale.setScalar(initialScale);
      root.position.set(surfPt.x, surfPt.y - localMinY * initialScale, surfPt.z);

      const boxHelper = new THREE.BoxHelper(root, 0xFDBA59);
      (boxHelper.material as THREE.LineBasicMaterial).depthTest = false;
      boxHelper.renderOrder = 998;

      const scene   = capturedViewer.scene as THREE.Scene;
      const camera  = capturedViewer.camera as THREE.Camera;
      const handles = makeHandles(root, camera, scene);
      scene.add(root, boxHelper);
      onObjectPlaced?.(root, cat.id);

      const placed: PlacedObject = {
        id: genId(), root, model, boxHelper, handles, rawSize, localMinY,
        surfaceY: surfPt.y, scale: initialScale, rotationY: 0,
        label: cat.label, icon: cat.icon ?? '✨', scene,
        shadingMode:   useTrellisUnlit ? 'unlit' : 'pbr',
        meshMaterials,
      };
      placedRef.current = [...placedRef.current, placed];
      setPlacedObjects([...placedRef.current]);
      setActiveAddId(placed.id);
      setAddStep('interactive');
    }, undefined, err => {
      if (loadRequestRef.current === requestId) {
        console.error('[EditSceneTool] load error:', err);
        setAddStep('capturing');
      }
    });
  }, [availableAssets, isAerial, selectedId, viewerRef]);

  // ── Scale / rotate add-mode object ────────────────────────────────────────
  const scaleActive = useCallback((factor: number) => {
    const obj = placedRef.current.find(p => p.id === activeAddId);
    if (!obj) return;
    const newScale = Math.max(0.01, obj.scale * factor);
    obj.root.scale.setScalar(newScale);
    obj.scale = newScale;
    obj.root.position.y = obj.surfaceY - obj.localMinY * newScale;
    obj.boxHelper.update();
    repositionHandles(obj.handles, obj.root);
    setPlacedObjects(prev => prev.map(p => p.id === activeAddId ? { ...p, scale: newScale } : p));
  }, [activeAddId]);

  const rotateActive = useCallback((delta: number) => {
    const obj = placedRef.current.find(p => p.id === activeAddId);
    if (!obj) return;
    const newRot        = (obj.rotationY + delta + 360) % 360;
    obj.rotationY       = newRot;
    obj.root.rotation.y = THREE.MathUtils.degToRad(newRot);
    obj.boxHelper.update();
    repositionHandles(obj.handles, obj.root);
    setPlacedObjects(prev => prev.map(p => p.id === activeAddId ? { ...p, rotationY: newRot } : p));
  }, [activeAddId]);

  const removeObject = useCallback((id: string) => {
    if (activeDragRef.current?.objectId === id) cancelDrag();
    const obj = placedRef.current.find(p => p.id === id);
    if (obj) disposePlacedObject(obj);
    placedRef.current = placedRef.current.filter(p => p.id !== id);
    setPlacedObjects([...placedRef.current]);
    if (placedRef.current.length === 0) { setAddStep('selecting'); setActiveAddId(null); }
    else if (activeAddId === id) setActiveAddId(placedRef.current[0].id);
  }, [activeAddId, cancelDrag]);

  const applyShadingMode = useCallback((id: string, mode: ShadingMode) => {
    const obj = placedRef.current.find(p => p.id === id);
    if (!obj) return;
    for (const mm of obj.meshMaterials) {
      mm.mesh.material = mode === 'unlit' ? mm.unlit : mm.pbr;
    }
    obj.shadingMode = mode;
    setPlacedObjects(prev => prev.map(p => p.id === id ? { ...p, shadingMode: mode } : p));
  }, []);

  const nudgeObject = useCallback((id: string, dir: 'forward' | 'backward' | 'left' | 'right' | 'up' | 'down') => {
    const obj = placedRef.current.find(p => p.id === id);
    if (!obj) return;
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const cam    = viewer?.camera as THREE.Camera | undefined;
    if (!cam) return;

    const STEP = 0.5; // metres per click

    if (dir === 'up' || dir === 'down') {
      obj.root.position.y += dir === 'up' ? STEP : -STEP;
      obj.boxHelper.update();
      repositionHandles(obj.handles, obj.root);
      return;
    }

    // Camera forward projected onto ground plane.
    const fwd = new THREE.Vector3();
    cam.getWorldDirection(fwd);
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
    fwd.normalize();
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();

    const delta = dir === 'forward'  ?  fwd.multiplyScalar( STEP) :
                  dir === 'backward' ?  fwd.multiplyScalar(-STEP) :
                  dir === 'right'    ? right.multiplyScalar( STEP) :
                                       right.multiplyScalar(-STEP);
    obj.root.position.add(delta);
    obj.boxHelper.update();
    repositionHandles(obj.handles, obj.root);
  }, [viewerRef]);

  // ── Erase: brush paint handlers ───────────────────────────────────────────
  // Cursor position is written directly to the DOM — no React re-render needed
  // per mouse move, which is the main cause of painting lag.
  const moveCursor = useCallback((x: number, y: number) => {
    const el = cursorRef.current;
    if (!el) return;
    el.style.display = 'block';
    el.style.left    = `${x}px`;
    el.style.top     = `${y}px`;
  }, []);

  const handleBrushDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isPaintingRef.current       = true;
    lastScanPxRef.current       = null;
    currentStrokeSetRef.current = new Set();
    pendingPaintRef.current     = { x: e.clientX, y: e.clientY };

    if (isAerial) {
      // Lock the elevation once at stroke start so every subsequent sample uses
      // a fixed horizontal plane — no repeated splat-BVH picks during drag.
      aerialLastWorldPosRef.current = null;
      const pt = pickWorldPointFromPointer(viewerRef.current, e.clientX, e.clientY);
      if (pt) {
        aerialLockedYRef.current = pt.y;
      } else {
        const cam = (viewerRef.current as Record<string, unknown>)?.camera as THREE.PerspectiveCamera | undefined;
        aerialLockedYRef.current = cam ? cam.position.y - 50 : 0;
      }
      const el = aerialCircleRef.current;
      if (el) {
        const d = erasePixelRadiusRef.current * 2;
        el.style.width   = `${d}px`;
        el.style.height  = `${d}px`;
        el.style.left    = `${e.clientX}px`;
        el.style.top     = `${e.clientY}px`;
        el.style.display = 'block';
      }
    } else {
      moveCursor(e.clientX, e.clientY);
      if (cursorRef.current) cursorRef.current.dataset.painting = 'true';
    }
  }, [moveCursor, isAerial, viewerRef]);

  const handleBrushMove = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    if (isAerial) {
      const el = aerialCircleRef.current;
      if (el) {
        el.style.left    = `${e.clientX}px`;
        el.style.top     = `${e.clientY}px`;
        el.style.display = 'block';
      }
    } else {
      moveCursor(e.clientX, e.clientY);
    }
    if (!isPaintingRef.current) return;
    pendingPaintRef.current = { x: e.clientX, y: e.clientY };
  }, [moveCursor, isAerial]);

  const handleBrushUp = useCallback(() => {
    isPaintingRef.current   = false;
    pendingPaintRef.current = null;
    if (cursorRef.current) delete cursorRef.current.dataset.painting;

    if (isAerial) {
      aerialLockedYRef.current      = null;
      aerialLastWorldPosRef.current = null;
    }

    if (currentStrokeSetRef.current.size === 0) return;

    const indices = [...currentStrokeSetRef.current];
    for (const idx of indices) allMaskedSetRef.current.add(idx);
    currentStrokeSetRef.current = new Set();

    // Always flush the final mask state on pointer-up — the throttled RAF loop
    // may have skipped the last <250 ms of stroke data.
    const viewer = viewerRef.current as Record<string, unknown> | null;
    const sm     = viewer?.splatMesh as SplatMesh | undefined;
    if (sm) {
      lastMaskUploadRef.current = performance.now();
      sm.setSplatMask(new Uint32Array([...allMaskedSetRef.current]));
    }

    const zone: EraseZone = { id: genId(), indices };
    eraseRef.current = [...eraseRef.current, zone];
    setEraseZones([...eraseRef.current]);
    setActiveEraseId(zone.id);
  }, [viewerRef, isAerial]);

  const handleBrushLeave = useCallback(() => {
    if (isAerial) {
      if (aerialCircleRef.current) aerialCircleRef.current.style.display = 'none';
    } else {
      if (cursorRef.current) cursorRef.current.style.display = 'none';
    }
  }, [isAerial]);

  // Scroll wheel adjusts aerial brush pixel radius.
  const handleEraseWheel = useCallback((e: React.WheelEvent) => {
    if (!isAerial) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -3 : 3;
    erasePixelRadiusRef.current = Math.max(10, Math.min(150, erasePixelRadiusRef.current + delta));
    const el = aerialCircleRef.current;
    if (el) {
      const d = erasePixelRadiusRef.current * 2;
      el.style.width  = `${d}px`;
      el.style.height = `${d}px`;
    }
  }, [isAerial]);

  const removeEraseZone = useCallback((id: string) => {
    eraseRef.current = eraseRef.current.filter(z => z.id !== id);
    // Rebuild cumulative mask set from remaining zones.
    allMaskedSetRef.current = new Set(eraseRef.current.flatMap(z => z.indices));

    const sm = (viewerRef.current as Record<string, unknown>)?.splatMesh as SplatMesh | undefined;
    if (sm) rebuildMask(sm, eraseRef.current);

    setEraseZones([...eraseRef.current]);
    if (activeEraseId === id)
      setActiveEraseId(eraseRef.current.length > 0 ? eraseRef.current[0].id : null);
  }, [activeEraseId, viewerRef]);

  // ── Paint brush handlers ──────────────────────────────────────────────────
  const movePaintCursor = useCallback((x: number, y: number) => {
    const el = paintCursorRef.current;
    if (!el) return;
    el.style.display = 'block';
    el.style.left    = `${x}px`;
    el.style.top     = `${y}px`;
  }, []);

  const handlePaintDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isPaintingPaintRef.current    = true;
    lastPaintPxRef.current        = null;
    lastPaintWldRef.current       = null;
    currentPaintMeshesRef.current = [];
    pendingPaintPosRef.current    = { x: e.clientX, y: e.clientY };
    movePaintCursor(e.clientX, e.clientY);
  }, [movePaintCursor]);

  const handlePaintMove = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    movePaintCursor(e.clientX, e.clientY);
    if (!isPaintingPaintRef.current) return;
    pendingPaintPosRef.current = { x: e.clientX, y: e.clientY };
  }, [movePaintCursor]);

  const handlePaintUp = useCallback(() => {
    isPaintingPaintRef.current = false;
    pendingPaintPosRef.current = null;

    const meshes = currentPaintMeshesRef.current;
    currentPaintMeshesRef.current = [];
    if (meshes.length === 0) return;

    const viewer = viewerRef.current as Record<string, unknown> | null;
    const scene  = viewer?.scene as THREE.Scene | undefined;
    if (!scene) return;

    const zone: PaintZone = { id: genId(), color: paintColorRef.current, meshes, scene };
    paintZonesRef.current = [...paintZonesRef.current, zone];
    setPaintZones([...paintZonesRef.current]);
    setActivePaintId(zone.id);
  }, [viewerRef]);

  const handlePaintLeave = useCallback(() => {
    if (paintCursorRef.current) paintCursorRef.current.style.display = 'none';
  }, []);

  const removePaintZone = useCallback((id: string) => {
    const zone = paintZonesRef.current.find(z => z.id === id);
    if (zone) {
      for (const m of zone.meshes) {
        zone.scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    }
    paintZonesRef.current = paintZonesRef.current.filter(z => z.id !== id);
    setPaintZones([...paintZonesRef.current]);
    if (activePaintId === id)
      setActivePaintId(paintZonesRef.current.length > 0 ? paintZonesRef.current[0].id : null);
  }, [activePaintId]);

  // ── Lasso (pencil) tool ───────────────────────────────────────────────────
  const moveLassoCursor = useCallback((x: number, y: number) => {
    const el = lassoCursorRef.current;
    if (!el) return;
    el.style.display = 'block';
    el.style.left    = `${x}px`;
    el.style.top     = `${y}px`;
  }, []);

  const handleLassoDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isDrawingLassoRef.current = true;
    lassoPointsRef.current    = [{ x: e.clientX, y: e.clientY }];
    setLassoPts([{ x: e.clientX, y: e.clientY }]);
    setLassoReady(false);
    moveLassoCursor(e.clientX, e.clientY);
  }, [moveLassoCursor]);

  const handleLassoMove = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    moveLassoCursor(e.clientX, e.clientY);
    if (!isDrawingLassoRef.current) return;
    const prev = lassoPointsRef.current;
    const last = prev[prev.length - 1];
    // Skip if barely moved — reduces point count without losing accuracy.
    if (last && Math.abs(e.clientX - last.x) < 3 && Math.abs(e.clientY - last.y) < 3) return;
    const next = [...prev, { x: e.clientX, y: e.clientY }];
    lassoPointsRef.current = next;
    setLassoPts(next);
  }, [moveLassoCursor]);

  const handleLassoUp = useCallback(() => {
    if (!isDrawingLassoRef.current) return;
    isDrawingLassoRef.current = false;
    if (lassoPointsRef.current.length >= 6) {
      setLassoReady(true);
    } else {
      lassoPointsRef.current = [];
      setLassoPts([]);
    }
  }, []);

  const handleLassoLeave = useCallback(() => {
    if (lassoCursorRef.current) lassoCursorRef.current.style.display = 'none';
  }, []);

  const clearLasso = useCallback(() => {
    lassoPointsRef.current = [];
    setLassoPts([]);
    setLassoReady(false);
  }, []);

  const fillLasso = useCallback(() => {
    const pts = lassoPointsRef.current;
    if (pts.length < 6) return;

    const viewer = viewerRef.current as Record<string, unknown> | null;
    const scene  = viewer?.scene as THREE.Scene | undefined;
    if (!scene) return;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }

    const step = 14; // pixels between sample points
    const worldPts: { x: number; y: number; z: number }[] = [];
    for (let px = minX; px <= maxX; px += step) {
      for (let py = minY; py <= maxY; py += step) {
        if (!pointInPolygon({ x: px, y: py }, pts)) continue;
        const pt = pickWorldPointFromPointer(viewerRef.current, px, py);
        if (pt) worldPts.push(pt);
      }
    }
    if (worldPts.length === 0) return;

    const r     = paintBrushRadiusRef.current;
    const color = paintColorRef.current;
    const geom  = new THREE.CircleGeometry(r, 12);
    const mat   = new THREE.MeshBasicMaterial({ color, depthWrite: false, transparent: true, opacity: 0.88 });
    const inst  = new THREE.InstancedMesh(geom, mat, worldPts.length);
    inst.renderOrder = 5;
    const dummy = new THREE.Object3D();
    worldPts.forEach(({ x, y, z }, i) => {
      dummy.rotation.x = -Math.PI / 2;
      dummy.position.set(x, y + 0.02, z);
      dummy.updateMatrix();
      inst.setMatrixAt(i, dummy.matrix);
    });
    inst.instanceMatrix.needsUpdate = true;
    scene.add(inst);

    const zone: PaintZone = { id: genId(), color, meshes: [inst as unknown as THREE.Mesh], scene };
    paintZonesRef.current = [...paintZonesRef.current, zone];
    setPaintZones([...paintZonesRef.current]);
    setActivePaintId(zone.id);
    clearLasso();
  }, [viewerRef, clearLasso]);

  // ── Done: hide gizmos, keep objects and mask ──────────────────────────────
  const handleDone = useCallback(() => {
    console.log('[EditSceneTool] handleDone → onClose');
    for (const p of placedRef.current) {
      p.boxHelper.visible = false;
      p.handles.forEach(h => { h.visible = false; });
    }
    const placed = placedRef.current;
    if (placed.length > 0 && onModelPlaced) {
      const target = placed.find(p => p.id === activeAddId) ?? placed[placed.length - 1];
      const pos = target.root.position;
      onModelPlaced([pos.x, pos.y, pos.z]);
    }
    onClose();
  }, [onClose, onModelPlaced, activeAddId]);

  // ── Mode switch ───────────────────────────────────────────────────────────
  const switchMode = useCallback((m: ToolMode) => {
    cancelDrag();
    if (m !== 'lasso') clearLasso();
    setMode(m);
  }, [cancelDrag, clearLasso]);

  if (!isActive) return null;

  const activeObj  = placedObjects.find(p => p.id === activeAddId) ?? null;

  return (
    <>
      {/* Placement capture overlay (add mode) */}
      {mode === 'add' && addStep === 'capturing' && (
        <>
          <div
            className="fixed inset-0 z-[1001]"
            style={{ cursor: 'crosshair', pointerEvents: 'auto', background: 'transparent' }}
            onPointerDown={handlePlaceDown}
            onPointerUp={handlePlaceUp}
            onPointerCancel={() => { placeDownRef.current = null; }}
          />
          <style>{`canvas { cursor: crosshair !important; }`}</style>
        </>
      )}

      {/* Erase brush overlay */}
      {mode === 'erase' && (
        <>
          {isBuildingGrid && (
            <div className="fixed bottom-24 left-1/2 z-[1010] -translate-x-1/2 rounded-lg bg-black/70 px-4 py-2 text-sm text-white">
              Preparing eraser…
            </div>
          )}
          <div
            className="fixed inset-0 z-[1001]"
            style={{ cursor: 'none', pointerEvents: isBuildingGrid ? 'none' : 'auto', background: 'transparent' }}
            onPointerDown={handleBrushDown}
            onPointerMove={handleBrushMove}
            onPointerUp={handleBrushUp}
            onPointerCancel={handleBrushUp}
            onPointerLeave={handleBrushLeave}
            onWheel={handleEraseWheel}
          />
          {/* Aerial: circle cursor sized in screen-space pixels, scroll to resize */}
          {isAerial && (
            <div
              ref={aerialCircleRef}
              className="fixed pointer-events-none z-[1004]"
              style={{
                display:      'none',
                left:         0,
                top:          0,
                width:        `${erasePixelRadiusRef.current * 2}px`,
                height:       `${erasePixelRadiusRef.current * 2}px`,
                border:       '2px solid rgba(255, 80, 80, 0.9)',
                borderRadius: '50%',
                transform:    'translate(-50%, -50%)',
                boxShadow:    '0 0 6px rgba(0,0,0,0.5)',
              }}
            />
          )}
          {/* Non-aerial: SVG eraser cursor — position written directly via cursorRef */}
          {!isAerial && (
            <div
              ref={cursorRef}
              className="fixed pointer-events-none z-[1004]"
              style={{ display: 'none', left: 0, top: 0, transform: 'translate(-5px, -24px)' }}
            >
              <svg
                width="36" height="44"
                viewBox="-5.0 -10.0 110.0 135.0"
                style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7)) drop-shadow(0 0 1px rgba(0,0,0,0.9))' }}
              >
                <path
                  d="m24.668 48.641-15.449 15.363c-1.1719 1.1641-1.1836 3.0586-0.023438 4.2383l18.68 18.961c0.5625 0.57422 1.3359 0.89453 2.1406 0.89453h14.531c0.79688 0 1.5586-0.31641 2.125-0.87891l8.2891-8.2891z"
                  fill="rgba(255,255,255,0.65)"
                />
                <path
                  d="m90.797 38.867-25.812-26.074c-0.58594-0.59375-1.3594-0.89062-2.1328-0.89062-0.76562 0-1.5312 0.29297-2.1172 0.875l-33.023 32.84 30.281 30.281 32.797-32.797c1.168-1.168 1.1719-3.0625 0.007813-4.2344z"
                  fill="rgba(255,255,255,0.92)"
                />
              </svg>
            </div>
          )}
          <style>{`canvas { cursor: none !important; }`}</style>
        </>
      )}

      {/* Paint brush overlay */}
      {mode === 'paint' && (
        <>
          <div
            className="fixed inset-0 z-[1001]"
            style={{ cursor: 'none', pointerEvents: 'auto', background: 'transparent' }}
            onPointerDown={handlePaintDown}
            onPointerMove={handlePaintMove}
            onPointerUp={handlePaintUp}
            onPointerCancel={handlePaintUp}
            onPointerLeave={handlePaintLeave}
          />
          {/* Paint brush SVG cursor — filled with selected paint color */}
          <div
            ref={paintCursorRef}
            className="fixed pointer-events-none z-[1004]"
            style={{ display: 'none', left: 0, top: 0, transform: 'translate(-5px, -34px)' }}
          >
            <svg
              width="34" height="42"
              viewBox="0 0 16.933333 21.1666675"
              style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8)) drop-shadow(0 0 1px rgba(0,0,0,0.9))' }}
            >
              <path
                d="m 14.193706,0.79390119 c -0.49121,0 -0.98244,0.18709 -1.3566,0.56120001 a 0.26460921,0.26460921 0 0 0 -0.008,0.009 L 4.7910665,10.179581 c -0.21105,-0.072 -0.42803,-0.11716 -0.64767,-0.13231 -0.0844,-0.006 -0.16901,-0.007 -0.25367,-0.004 v -7e-5 c -0.59235,0.0237 -1.18488,0.27412 -1.683,0.7719 a 0.26460921,0.26460921 0 0 0 -10e-4,5.3e-4 c -0.62132,0.62125 -0.6387,1.56679 -0.69312,2.47857 -0.0544,0.91178 -0.13301,1.80093 -0.64442001,2.41081 a 0.26460921,0.26460921 0 0 0 0.21604001,0.43424 c 0,0 0.94679,-0.0473 2.02147,-0.22028 0.53734,-0.0865 1.10782,-0.20413 1.61678,-0.36684 0.50893,-0.16271 0.95855,-0.36213 1.26148,-0.665 h 5e-5 c 0.74429,-0.74367 0.98285,-1.80498 0.71348,-2.74785 L 15.540636,4.0777812 c 0.80082,-0.71775 0.75902,-1.97441 0.0105,-2.72283 -0.37417,-0.37412001 -0.8654,-0.56121001 -1.3566,-0.56120001 z m 10e-4,0.52489001 c 0.35397,2e-4 0.70787,0.13702 0.98136,0.41048 0.54729,0.54724 0.57425,1.44931 0.0105,1.9546 a 0.26460921,0.26460921 0 0 0 -0.002,0.002 l -6.7529195,6.15599 -1.36894,-1.3688 6.1498595,-6.74468 c 0.27379,-0.27294 0.62784,-0.40933 0.98182,-0.40912 z m -7.4885895,7.54522 1.33438,1.3342398 -1.55401,1.41664 c -0.12588,-0.23938 -0.28916,-0.46392 -0.49007,-0.66481 -0.21518,-0.21514 -0.4506,-0.39709 -0.69969,-0.54036 z m -2.74774,1.7063898 c 0.0494,-4.1e-4 0.0988,10e-4 0.14819,0.005 0.21569,0.0149 0.43286,0.0674 0.6452,0.1551 a 0.26458275,0.26458275 0 0 0 0.009,0.004 c 0.30355,0.12689 0.597,0.32582 0.86169,0.59047 0.24468,0.24466 0.42082,0.53157 0.52857,0.83733 a 0.26458275,0.26458275 0 0 0 0.003,0.007 c 0.2757,0.7907 0.0944,1.70626 -0.54427,2.34437 a 0.26460921,0.26460921 0 0 0 -1.3e-4,0 c -0.1908,0.19078 -0.57873,0.38505 -1.04846,0.53522 -0.46974,0.15018 -1.01906,0.26464 -1.53979,0.34848 -0.58013,0.0934 -1.07257,0.14388 -1.43998,0.17501 0.3614,-0.69481 0.41501,-1.50474 0.45923,-2.24579 0.0547,-0.91622 0.11647,-1.71278 0.53864,-2.13541 a 0.26460921,0.26460921 0 0 0 10e-4,-4.2e-4 c 0.41515,-0.41511 0.86911,-0.6026 1.3293,-0.61862 v 8e-5 c 0.0164,-5.7e-4 0.0329,-9.3e-4 0.0493,-0.001 z"
                fill={paintColor}
                fillRule="evenodd"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.3"
              />
            </svg>
          </div>
          <style>{`canvas { cursor: none !important; }`}</style>
        </>
      )}

      {/* Lasso (draw) overlay */}
      {mode === 'lasso' && (
        <>
          <div
            className="fixed inset-0 z-[1001]"
            style={{ cursor: 'none', pointerEvents: 'auto', background: 'transparent' }}
            onPointerDown={handleLassoDown}
            onPointerMove={handleLassoMove}
            onPointerUp={handleLassoUp}
            onPointerCancel={handleLassoUp}
            onPointerLeave={handleLassoLeave}
          />
          {/* Live lasso path — drawn as SVG over the scene */}
          {lassoPts.length > 1 && (
            <svg
              className="fixed pointer-events-none z-[1002]"
              style={{ top: 0, left: 0, width: '100vw', height: '100vh', overflow: 'visible' }}
            >
              <defs>
                <filter id="lasso-glow">
                  <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="rgba(255,255,255,0.6)" />
                </filter>
              </defs>
              {/* Fill preview when ready */}
              {lassoReady && (
                <polygon
                  points={lassoPts.map(p => `${p.x},${p.y}`).join(' ')}
                  fill={`${paintColor}30`}
                  stroke="none"
                />
              )}
              {/* Outline */}
              <polyline
                points={lassoPts.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="white"
                strokeWidth="1.5"
                strokeDasharray={lassoReady ? undefined : '7 3'}
                strokeLinejoin="round"
                strokeLinecap="round"
                filter="url(#lasso-glow)"
              />
              {/* Closing segment */}
              {lassoReady && (
                <line
                  x1={lassoPts[lassoPts.length - 1].x} y1={lassoPts[lassoPts.length - 1].y}
                  x2={lassoPts[0].x}                   y2={lassoPts[0].y}
                  stroke="white" strokeWidth="1.5" filter="url(#lasso-glow)"
                />
              )}
            </svg>
          )}
          {/* Pencil cursor */}
          <div
            ref={lassoCursorRef}
            className="fixed pointer-events-none z-[1004]"
            style={{ display: 'none', left: 0, top: 0, transform: 'translate(-8px, -33px)' }}
          >
            <svg
              width="40" height="50"
              viewBox="0 0 6.82666 8.533325"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.9))' }}
            >
              <path className="fil0" d="M4.81057 1.17098l0.845449 0.845449 3.93701e-006 -3.93701e-006c0.0208268,0.0208268 0.0208268,0.0545984 0,0.0754252l-0.000326772 0.000318898 -3.49553 3.49552 3.93701e-006 3.93701e-006c-0.00762992,0.00762992 -0.0169961,0.0124606 -0.0268268,0.0145l-1.21122 0.36863 6.29921e-005 0.000204724c-0.0281772,0.0085748 -0.0579764,-0.0073189 -0.0665512,-0.0354961 -0.00369685,-0.0121496 -0.00284646,-0.0245945 0.00157874,-0.0355157l0.368634 -1.21122 0.0508346 0.0154173 -0.0510236 -0.0155276c0.00275591,-0.00905118 0.00769685,-0.0168307 0.0140748,-0.0229409l3.49509 -3.49509 -3.93701e-006 -3.93701e-006c0.0208268,-0.0208268 0.0545984,-0.0208268 0.0754252,0l0.000318898 0.000326772zm0.732323 0.883157l-0.770354 -0.770354 -3.44902 3.44902 -0.337031 1.10739 1.10739 -0.337031 3.44902 -3.44902z" fill="white" fillRule="nonzero" />
              <path className="fil0" d="M5.34007 0.92824l0.558346 0.558346c0.0499331,0.0499331 0.0749055,0.115705 0.0749055,0.181421 0,0.0657205 -0.0249724,0.131492 -0.0749055,0.181425l-0.416244 0.416244 3.93701e-006 3.93701e-006c-0.0208268,0.0208268 -0.0545984,0.0208268 -0.0754252,0l-0.000318898 -0.000326772 -0.845453 -0.845453 -3.93701e-006 3.93701e-006c-0.0208268,-0.0208268 -0.0208268,-0.0545984 0,-0.0754252l0.000326772 -0.000318898 0.415921 -0.415921c0.049937,-0.049937 0.115709,-0.0749094 0.181425,-0.0749094 0.0657165,0 0.131488,0.0249724 0.181421,0.0749094zm0.482929 0.633764l-0.558346 -0.558346c-0.0290984,-0.0291024 -0.0675354,-0.0436575 -0.106004,-0.0436575 -0.0384685,0 -0.0769055,0.0145551 -0.106008,0.0436575l-0.378535 0.378535 0.770358 0.770358 0.378535 -0.378535c0.0290984,-0.0290984 0.0436535,-0.0675354 0.0436535,-0.106008 0,-0.0384685 -0.0145551,-0.0769055 -0.0436535,-0.106004z" fill="white" fillRule="nonzero" />
              <path className="fil0" d="M2.08475 5.58769c0.0208228,0.0208228 0.0545945,0.0208228 0.0754173,0 0.0208228,-0.0208228 0.0208228,-0.0545945 0,-0.0754173l-0.845772 -0.845772c-0.0208228,-0.0208228 -0.0545945,-0.0208228 -0.0754173,0 -0.0208228,0.0208228 -0.0208228,0.0545945 0,0.0754173l0.845772 0.845772z" fill="white" fillRule="nonzero" />
              <path className="fil0" d="M4.49063 2.41145c0.0208228,-0.0208228 0.0208228,-0.0545945 0,-0.0754173 -0.0208228,-0.0208228 -0.0545945,-0.0208228 -0.0754173,0l-2.22016 2.22016c-0.0208228,0.0208228 -0.0208228,0.0545945 0,0.0754173 0.0208228,0.0208228 0.0545945,0.0208228 0.0754173,0l2.22016 -2.22016z" fill="white" fillRule="nonzero" />
            </svg>
          </div>
          <style>{`canvas { cursor: none !important; }`}</style>
        </>
      )}

      {/* Toolbox panel */}
      <div
        ref={panelRef}
        className="fixed z-[1003] rounded-xl border border-white/10 bg-black/90 p-3 text-white shadow-2xl backdrop-blur-md"
        style={{
          top:   anchorRef?.current ? anchorRef.current.getBoundingClientRect().bottom + 12 : 12,
          right: anchorRef?.current ? 16 : 76,
          width: 260,
        }}
        onPointerDown={e => e.stopPropagation()}
        onPointerMove={e => e.stopPropagation()}
        onPointerUp={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
              <path d="M4 9V6M4 6H7M4 6H1"/><path d="M12 7v3m0 0v3m0-3h3m-3 0H9"/>
            </svg>
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-white/50">Toolbox</h3>
          </div>
          <button
            className="w-5 h-5 flex items-center justify-center rounded text-white/30 hover:text-white hover:bg-white/10 text-xs transition-colors"
            onClick={onClose}
          >✕</button>
        </div>

        {/* Tool icon strip */}
        <div className="grid grid-cols-4 gap-1 mb-3">
          {/* Erase */}
          <button
            onClick={() => switchMode('erase')}
            title="Erase"
            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-all ${
              mode === 'erase'
                ? 'border-red-400/50 bg-red-400/15 text-red-300'
                : 'border-white/5 bg-white/5 text-white/35 hover:text-white/70 hover:bg-white/10 hover:border-white/10'
            }`}
          >
            <svg viewBox="0 0 22 22" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 17 L17 5 L21 9 L11 21 H6 Z" fill="currentColor" fillOpacity="0.2"/>
              <path d="M5 17 L17 5 L21 9 L11 21 H6 Z"/>
              <line x1="10" y1="21" x2="21" y2="21"/>
              <line x1="14" y1="7" x2="18" y2="11"/>
            </svg>
            <span className="text-[8px] font-semibold tracking-wide leading-none">ERASE</span>
          </button>

          {/* Paint */}
          <button
            onClick={() => switchMode('paint')}
            title="Paint brush"
            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-all ${
              mode === 'paint'
                ? 'border-green-400/50 bg-green-400/15 text-green-300'
                : 'border-white/5 bg-white/5 text-white/35 hover:text-white/70 hover:bg-white/10 hover:border-white/10'
            }`}
          >
            <svg viewBox="0 0 22 22" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3 L20 8 L8 18 C6.5 19.5 4 20 3 20 C3 19 3.5 16.5 5 15 Z"/>
              <line x1="12" y1="6" x2="17" y2="11"/>
              <circle cx="3.5" cy="19.5" r="1" fill="currentColor"/>
            </svg>
            <span className="text-[8px] font-semibold tracking-wide leading-none">PAINT</span>
          </button>

          {/* Draw / Lasso */}
          <button
            onClick={() => switchMode('lasso')}
            title="Draw & fill"
            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-all ${
              mode === 'lasso'
                ? 'border-violet-400/50 bg-violet-400/15 text-violet-300'
                : 'border-white/5 bg-white/5 text-white/35 hover:text-white/70 hover:bg-white/10 hover:border-white/10'
            }`}
          >
            <svg viewBox="0 0 22 22" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 2 L20 6 L7 19 L2 20 L3 15 Z"/>
              <line x1="13" y1="5" x2="17" y2="9"/>
            </svg>
            <span className="text-[8px] font-semibold tracking-wide leading-none">DRAW</span>
          </button>

          {/* Add objects */}
          <button
            onClick={() => switchMode('add')}
            title="Add objects"
            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-all ${
              mode === 'add'
                ? 'border-amber-400/50 bg-amber-400/15 text-amber-300'
                : 'border-white/5 bg-white/5 text-white/35 hover:text-white/70 hover:bg-white/10 hover:border-white/10'
            }`}
          >
            <svg viewBox="0 0 22 22" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 2 L20 7 V15 L11 20 L2 15 V7 Z"/>
              <line x1="11" y1="2" x2="11" y2="20"/>
              <path d="M2 7 L11 12 L20 7"/>
              <line x1="11" y1="7" x2="11" y2="2" stroke="currentColor" strokeWidth="0"/>
            </svg>
            <span className="text-[8px] font-semibold tracking-wide leading-none">ADD</span>
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/8 mb-3" />

        {/* ── ADD MODE ──────────────────────────────────────────────────────── */}
        {mode === 'add' && (
          <div className="space-y-3">
            {addStep === 'selecting' && (
              <div className="space-y-2">
                <p className="text-xs text-white/50">
                  {availableAssets.length > 0
                    ? 'Choose an object to place in the scene.'
                    : 'Generate or upload a 3D model to place it in the scene.'}
                </p>
                {availableAssets.map(obj => (
                  <button
                    key={obj.id}
                    onClick={() => { setSelectedId(obj.id); setAddStep('capturing'); }}
                    className="w-full flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-left hover:bg-white/10 hover:border-[#0CB4E4]/40 transition-colors"
                  >
                      <span className="text-xl">{obj.icon ?? '✨'}</span>
                    <span className="text-sm font-medium">{obj.label}</span>
                  </button>
                ))}
              </div>
            )}

            {addStep === 'capturing' && selectedId && (() => {
              const cat = availableAssets.find(o => o.id === selectedId);
              if (!cat) return null;
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 rounded-lg border border-[#0CB4E4]/30 bg-[#0CB4E4]/10 px-3 py-2">
                    <span className="text-base">{cat.icon}</span>
                    <span className="text-sm font-medium text-[#0CB4E4] truncate">{cat.label}</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-[#0CB4E4]/60 bg-[#0CB4E4]/15 px-3 py-2.5">
                    <span className="w-2 h-2 rounded-full bg-[#0CB4E4] animate-pulse shrink-0" />
                    <p className="text-sm font-semibold text-[#0CB4E4]">Click anywhere in the scene to place it</p>
                  </div>
                  <button className="text-xs text-white/40 hover:text-white/70" onClick={() => setAddStep('selecting')}>
                    ← Choose a different object
                  </button>
                </div>
              );
            })()}

            {addStep === 'loading' && (
              <div className="flex items-center gap-2 text-sm text-white/70">
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Loading model…</span>
              </div>
            )}

            {addStep === 'interactive' && (
              <>
                <p className="text-[10px] text-white/35 leading-relaxed">
                  Drag object body to move · Drag corner handle to resize
                </p>

                {activeObj && (
                  <div className="rounded-md border border-[#0CB4E4]/25 bg-[#0CB4E4]/5 p-2.5 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base leading-none">{activeObj.icon}</span>
                      <span className="text-xs font-medium text-white/80 flex-1 truncate">{activeObj.label}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button className="w-8 h-8 flex items-center justify-center rounded border border-white/10 bg-white/5 text-lg font-semibold hover:bg-white/15 active:scale-95 transition-all" onClick={() => scaleActive(1 / 1.1)}>−</button>
                      <span className="flex-1 text-center text-[11px] font-mono text-white/60 bg-white/5 rounded border border-white/10 py-1.5">
                        {(activeObj.rawSize.y * activeObj.scale).toFixed(2)} m
                      </span>
                      <button className="w-8 h-8 flex items-center justify-center rounded border border-white/10 bg-white/5 text-lg font-semibold hover:bg-white/15 active:scale-95 transition-all" onClick={() => scaleActive(1.1)}>+</button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button className="flex-1 rounded border border-white/10 bg-white/5 py-1.5 text-xs hover:bg-white/10 transition-colors" onClick={() => rotateActive(-15)}>↺ 15°</button>
                      <span className="w-10 text-center text-[10px] font-mono text-white/40">{activeObj.rotationY}°</span>
                      <button className="flex-1 rounded border border-white/10 bg-white/5 py-1.5 text-xs hover:bg-white/10 transition-colors" onClick={() => rotateActive(15)}>↻ 15°</button>
                    </div>
                    {/* Nudge: move along camera axes on the ground plane */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wide w-12 shrink-0">Move</span>
                      <div className="flex-1 grid grid-cols-4 gap-1">
                        {([ ['←', 'left'], ['↑', 'forward'], ['↓', 'backward'], ['→', 'right'] ] as [string, 'left'|'forward'|'backward'|'right'][]).map(([arrow, dir]) => (
                          <button
                            key={dir}
                            title={dir}
                            onClick={() => nudgeObject(activeObj.id, dir)}
                            className="flex items-center justify-center rounded border border-white/10 bg-white/5 py-1.5 text-sm hover:bg-white/15 active:scale-95 transition-all"
                          >{arrow}</button>
                        ))}
                      </div>
                    </div>
                    {/* Nudge: raise / lower along world Y */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wide w-12 shrink-0">Height</span>
                      <div className="flex-1 grid grid-cols-2 gap-1">
                        <button
                          title="raise"
                          onClick={() => nudgeObject(activeObj.id, 'up')}
                          className="flex items-center justify-center gap-1 rounded border border-white/10 bg-white/5 py-1.5 text-sm hover:bg-white/15 active:scale-95 transition-all"
                        >▲</button>
                        <button
                          title="lower"
                          onClick={() => nudgeObject(activeObj.id, 'down')}
                          className="flex items-center justify-center gap-1 rounded border border-white/10 bg-white/5 py-1.5 text-sm hover:bg-white/15 active:scale-95 transition-all"
                        >▼</button>
                      </div>
                    </div>
                    {activeObj.meshMaterials.some(mm => mm.unlit !== mm.pbr) && (
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <span className="text-[9px] text-white/35 font-semibold uppercase tracking-wide w-12 shrink-0">Shading</span>
                        <div className="flex-1 flex rounded border border-white/10 overflow-hidden text-[10px] font-semibold">
                          <button
                            className={`flex-1 py-1 transition-colors ${activeObj.shadingMode === 'unlit' ? 'bg-[#0CB4E4]/25 text-[#0CB4E4]' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                            onClick={() => applyShadingMode(activeObj.id, 'unlit')}
                          >Unlit</button>
                          <button
                            className={`flex-1 py-1 transition-colors ${activeObj.shadingMode === 'pbr' ? 'bg-[#0CB4E4]/25 text-[#0CB4E4]' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                            onClick={() => applyShadingMode(activeObj.id, 'pbr')}
                          >PBR</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {placedObjects.map(obj => {
                    const isSel = obj.id === activeAddId;
                    return (
                      <div
                        key={obj.id}
                        onClick={() => setActiveAddId(obj.id)}
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${isSel ? 'border-[#0CB4E4]/50 bg-[#0CB4E4]/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                      >
                        <span className="text-sm leading-none">{obj.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/80 truncate">{obj.label}</p>
                          <p className="text-[10px] font-mono text-white/40">{(obj.rawSize.y * obj.scale).toFixed(2)} m · {obj.rotationY}°</p>
                        </div>
                        <button
                          className="shrink-0 p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors"
                          onClick={ev => { ev.stopPropagation(); removeObject(obj.id); }}
                          title="Remove"
                        >
                          <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button className="w-full rounded-md bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/20" onClick={() => setAddStep('selecting')}>
                  Place another
                </button>
              </>
            )}
          </div>
        )}

        {/* ── ERASE MODE ────────────────────────────────────────────────────── */}
        {mode === 'erase' && (
          <div className="space-y-3">
            {/* AI-region banner — only when eraseRegion was provided */}
            {eraseRegion && (
              <div className="rounded-lg border border-[#0CB4E4]/30 bg-[#0CB4E4]/8 px-3 py-2.5">
                <p className="text-xs text-[#0CB4E4] font-medium mb-1">AI-selected region</p>
                <p className="text-[10px] text-white/45 leading-relaxed mb-2.5">
                  The region has been pre-selected ({eraseRegion.radius.toFixed(1)} m radius). Refine further with the brush, or confirm the erase.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { onEraseRegionDismissed?.(); }}
                    className="rounded border border-white/15 bg-white/8 px-3 py-1.5 text-[11px] text-white/60 hover:bg-white/15 transition-colors"
                  >
                    Reselect
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const lastBox = eraseBoxes && eraseBoxes.length > 0 ? eraseBoxes[eraseBoxes.length - 1] : null;
                      if (lastBox) setPendingVoidAabb({ min: lastBox.min, max: lastBox.max });
                      switchMode('paint');
                    }}
                    className="ml-auto rounded bg-red-500/75 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-500 transition-colors"
                  >
                    Erase Selected Region
                  </button>
                </div>
              </div>
            )}

            {/* Instruction */}
            {!eraseRegion && (
              <div className="rounded-lg border border-red-500/25 bg-red-500/8 px-3 py-2.5">
                <p className="text-xs text-red-300 font-medium mb-1">Paint to erase</p>
                <p className="text-[10px] text-white/45 leading-relaxed">
                  Click and drag over any area to remove those splats. Use the brush size slider to control how much is erased at once.
                </p>
              </div>
            )}

            {/* Brush size */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-white/50 font-semibold uppercase tracking-wide">Brush size</label>
                <span className="text-[11px] font-mono text-white/60">{brushRadius.toFixed(1)} m</span>
              </div>
              <input
                type="range"
                min="0.1" max="5" step="0.1"
                value={brushRadius}
                onChange={e => setBrushRadius(Number(e.target.value))}
                className="w-full accent-red-400 cursor-pointer"
              />
            </div>

            {/* Stroke list */}
            {eraseZones.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {eraseZones.map((zone, i) => {
                  const isSel = zone.id === activeEraseId;
                  return (
                    <div
                      key={zone.id}
                      onClick={() => setActiveEraseId(zone.id)}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${isSel ? 'border-red-500/50 bg-red-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    >
                      <svg viewBox="0 0 14 14" className="w-3.5 h-3.5 shrink-0 opacity-50" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="8" width="10" height="4" rx="1"/>
                        <path d="M5 8 L9 2 L12 5 L8 8"/>
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/80">Stroke {i + 1}</p>
                        <p className="text-[10px] font-mono text-white/40">{zone.indices.length.toLocaleString()} splats</p>
                      </div>
                      <button
                        className="shrink-0 p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors"
                        onClick={ev => { ev.stopPropagation(); removeEraseZone(zone.id); }}
                        title="Restore this stroke"
                      >
                        <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                          <line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {eraseZones.length === 0 && !eraseRegion && (
              <p className="text-[11px] text-white/25 text-center py-1">No erased strokes yet</p>
            )}
          </div>
        )}

        {/* ── PAINT MODE ────────────────────────────────────────────────────── */}
        {mode === 'paint' && (
          <div className="space-y-3">
            {/* Fill void prompt — shown when arriving from an erase confirmation */}
            {pendingVoidAabb && (
              <div className="rounded-lg border border-[#0CB4E4]/30 bg-[#0CB4E4]/8 px-3 py-2.5">
                <p className="text-xs text-[#0CB4E4] font-medium mb-1">Fill the void?</p>
                <p className="text-[10px] text-white/45 leading-relaxed mb-2.5">
                  The erased region left a void. Pick a surface color below and click Fill Void to cover it, or paint manually.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingVoidAabb(null)}
                    className="rounded border border-white/15 bg-white/8 px-3 py-1.5 text-[11px] text-white/60 hover:bg-white/15 transition-colors"
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const viewer = viewerRef.current as { splatMesh?: SplatMesh } | null;
                      const splatMesh = viewer?.splatMesh;
                      if (!splatMesh || !pendingVoidAabb) return;
                      // Color the already-masked splats instead of adding a Three.js overlay.
                      // setSplatMaskColor reuses the existing mask and paints those splats with
                      // the chosen fill color — seamlessly integrated into the splat render.
                      splatMesh.setSplatMaskColor({ color: paintColorRef.current });
                      setPendingVoidAabb(null);
                    }}
                    className="ml-auto rounded bg-[#0CB4E4]/80 px-3 py-1.5 text-[11px] font-semibold text-black hover:bg-[#0CB4E4] transition-colors"
                  >
                    Fill Void
                  </button>
                </div>
              </div>
            )}

            {/* Instruction — only when not in post-erase flow */}
            {!pendingVoidAabb && (
            <div className="rounded-lg border border-green-500/25 bg-green-500/8 px-3 py-2.5">
              <p className="text-xs text-green-300 font-medium mb-1">Paint to fill</p>
              <p className="text-[10px] text-white/45 leading-relaxed">
                Click and drag to paint color onto the scene. Useful for covering white voids left after erasing.
              </p>
            </div>
            )}

            {/* Color swatches + custom picker */}
            <div className="space-y-1.5">
              <label className="text-[10px] text-white/50 font-semibold uppercase tracking-wide">Color</label>
              <div className="flex flex-wrap gap-1.5 items-center">
                {([
                  { color: '#7a9a5a', label: 'Grass' },
                  { color: '#4a6741', label: 'Dark grass' },
                  { color: '#8c8070', label: 'Pavement' },
                  { color: '#9b7653', label: 'Dirt' },
                  { color: '#c8b89a', label: 'Sand' },
                  { color: '#5a6a50', label: 'Moss' },
                ] as { color: string; label: string }[]).map(({ color, label }) => (
                  <button
                    key={color}
                    title={label}
                    onClick={() => setPaintColor(color)}
                    className="w-7 h-7 rounded-full transition-transform hover:scale-110 active:scale-95"
                    style={{
                      background:  color,
                      outline:     paintColor === color ? '2px solid white' : '2px solid transparent',
                      outlineOffset: '1px',
                      boxShadow:   '0 1px 3px rgba(0,0,0,0.5)',
                    }}
                  />
                ))}
                {/* Custom color — native color input hidden behind swatch */}
                <label
                  title="Custom color"
                  className="relative w-7 h-7 rounded-full flex items-center justify-center text-white/70 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                  style={{
                    background:  'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)',
                    boxShadow:   '0 1px 3px rgba(0,0,0,0.5)',
                  }}
                >
                  <input
                    type="color"
                    value={paintColor}
                    onChange={e => setPaintColor(e.target.value)}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer rounded-full"
                  />
                </label>
              </div>
              {/* Show currently selected custom color if it's not a preset */}
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-4 h-4 rounded-sm shrink-0" style={{ background: paintColor, boxShadow: '0 1px 2px rgba(0,0,0,0.4)' }} />
                <span className="text-[10px] font-mono text-white/40">{paintColor}</span>
              </div>
            </div>

            {/* Brush size */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-white/50 font-semibold uppercase tracking-wide">Brush size</label>
                <span className="text-[11px] font-mono text-white/60">{paintBrushRadius.toFixed(2)} m</span>
              </div>
              <input
                type="range"
                min="0.05" max="3" step="0.05"
                value={paintBrushRadius}
                onChange={e => setPaintBrushRadius(Number(e.target.value))}
                className="w-full accent-green-400 cursor-pointer"
              />
            </div>

            {/* Stroke list */}
            {paintZones.length > 0 && (
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {paintZones.map((zone, i) => {
                  const isSel = zone.id === activePaintId;
                  return (
                    <div
                      key={zone.id}
                      onClick={() => setActivePaintId(zone.id)}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${isSel ? 'border-green-500/50 bg-green-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    >
                      <div className="w-3.5 h-3.5 rounded-full shrink-0 border border-white/20" style={{ background: zone.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/80">Stroke {i + 1}</p>
                        <p className="text-[10px] font-mono text-white/40">{zone.meshes.length} patches</p>
                      </div>
                      <button
                        className="shrink-0 p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors"
                        onClick={ev => { ev.stopPropagation(); removePaintZone(zone.id); }}
                        title="Remove this stroke"
                      >
                        <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                          <line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {paintZones.length === 0 && (
              <p className="text-[11px] text-white/25 text-center py-1">No painted strokes yet</p>
            )}
          </div>
        )}

        {/* ── DRAW (LASSO) MODE ─────────────────────────────────────────────── */}
        {mode === 'lasso' && (
          <div className="space-y-3">
            {/* Status banner */}
            <div className={`rounded-lg border px-3 py-2.5 ${lassoReady ? 'border-violet-400/40 bg-violet-500/10' : 'border-violet-500/25 bg-violet-500/8'}`}>
              <p className="text-xs text-violet-300 font-medium mb-1">
                {lassoReady ? 'Shape ready to fill' : 'Draw a shape'}
              </p>
              <p className="text-[10px] text-white/45 leading-relaxed">
                {lassoReady
                  ? 'Pick a color and hit Fill to paint the enclosed area.'
                  : 'Click and drag to draw a freehand lasso around the area you want to fill.'}
              </p>
            </div>

            {/* Fill color */}
            <div className="space-y-1.5">
              <label className="text-[10px] text-white/50 font-semibold uppercase tracking-wide">Fill color</label>
              <div className="flex flex-wrap gap-1.5 items-center">
                {([
                  { color: '#7a9a5a', label: 'Grass' },
                  { color: '#4a6741', label: 'Dark grass' },
                  { color: '#8c8070', label: 'Pavement' },
                  { color: '#9b7653', label: 'Dirt' },
                  { color: '#c8b89a', label: 'Sand' },
                  { color: '#5a6a50', label: 'Moss' },
                ] as { color: string; label: string }[]).map(({ color, label }) => (
                  <button
                    key={color}
                    title={label}
                    onClick={() => setPaintColor(color)}
                    className="w-7 h-7 rounded-full transition-transform hover:scale-110 active:scale-95"
                    style={{
                      background:    color,
                      outline:       paintColor === color ? '2px solid white' : '2px solid transparent',
                      outlineOffset: '1px',
                      boxShadow:     '0 1px 3px rgba(0,0,0,0.5)',
                    }}
                  />
                ))}
                <label
                  title="Custom color"
                  className="relative w-7 h-7 rounded-full cursor-pointer hover:scale-110 transition-transform"
                  style={{ background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)', boxShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
                >
                  <input type="color" value={paintColor} onChange={e => setPaintColor(e.target.value)}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer rounded-full" />
                </label>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-4 h-4 rounded-sm shrink-0" style={{ background: paintColor, boxShadow: '0 1px 2px rgba(0,0,0,0.4)' }} />
                <span className="text-[10px] font-mono text-white/40">{paintColor}</span>
              </div>
            </div>

            {/* Patch size (controls circle radius for fill sampling) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-white/50 font-semibold uppercase tracking-wide">Patch size</label>
                <span className="text-[11px] font-mono text-white/60">{paintBrushRadius.toFixed(2)} m</span>
              </div>
              <input type="range" min="0.05" max="3" step="0.05"
                value={paintBrushRadius} onChange={e => setPaintBrushRadius(Number(e.target.value))}
                className="w-full accent-violet-400 cursor-pointer" />
            </div>

            {/* Fill / Clear actions */}
            {lassoReady && (
              <div className="flex gap-2">
                <button
                  onClick={fillLasso}
                  className="flex-1 rounded-lg bg-violet-600/80 text-white text-xs font-semibold py-2 hover:bg-violet-600 transition-colors"
                >
                  Fill shape
                </button>
                <button
                  onClick={clearLasso}
                  className="rounded-lg border border-white/10 bg-white/5 text-white/60 text-xs font-semibold px-3 py-2 hover:bg-white/10 transition-colors"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Filled zone list */}
            {paintZones.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {paintZones.map((zone, i) => {
                  const isSel = zone.id === activePaintId;
                  return (
                    <div key={zone.id} onClick={() => setActivePaintId(zone.id)}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${isSel ? 'border-violet-500/50 bg-violet-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    >
                      <div className="w-3.5 h-3.5 rounded-full shrink-0 border border-white/20" style={{ background: zone.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/80">Fill {i + 1}</p>
                        <p className="text-[10px] font-mono text-white/40">{zone.meshes.length} patch{zone.meshes.length !== 1 ? 'es' : ''}</p>
                      </div>
                      <button className="shrink-0 p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors"
                        onClick={ev => { ev.stopPropagation(); removePaintZone(zone.id); }} title="Remove">
                        <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                          <line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {paintZones.length === 0 && (
              <p className="text-[11px] text-white/25 text-center py-1">No fills yet</p>
            )}
          </div>
        )}

        {/* Done — shared across modes */}
        <button
          className="w-full mt-4 rounded-lg bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black hover:bg-[#fcc570] transition-colors"
          onClick={handleDone}
        >
          Done
        </button>
      </div>
    </>
  );
};

export default EditSceneTool;
